import dicomParser from "dicom-parser";
import axiosInterceptor from "../../../helpers/Interceptor";
import {
  getViewport,
  getViewportByIdAndDefRenderingEngine,
  getMixedSeriesInstances,
} from "./CornerstoneViewportService";
import { getImageMetadata } from "./MultiFrameService";
import { getImageFrameIndex } from "./DicomOverlayPlaneService";
import * as cornerstone from "@cornerstonejs/core";
import * as cornerstonetools from "@cornerstonejs/tools";
import { annotation } from "@cornerstonejs/tools";
import { generateDicomUID } from "./DicomUidUtility";

// Cache parsed PR data by URL to avoid re-parsing on each viewport load
const prCache = new Map();

// When a PR freehand annotation that has suppressStats:true is moved, Cornerstone
// sets invalidated:true and _calculateStatsIfActive wipes its cachedStats (it
// checks for `.unit`, finds none, reinitializes to nulls). This makes the freehand
// body "vanish" (empty stats → _renderStats shows nothing). Fix: listen for
// ANNOTATION_MODIFIED and immediately reset invalidated:false on suppressed
// annotations so the stat-wipe path never triggers.
const _onAnnotationModified = (evt) => {
  try {
    const ann = evt.detail?.annotation;
    if (!ann) return;
    if (ann.data?.suppressStats && ann.invalidated) {
      ann.invalidated = false;
    }
  } catch (_) {}
};
try {
  document.addEventListener(
    cornerstonetools.Enums.Events.ANNOTATION_MODIFIED,
    _onAnnotationModified
  );
} catch (_) {}

// Track annotation UIDs per imageId (for pencil toggle / removeAll)
const prAnnotationsByImageId = new Map();

// Track annotation UIDs per PR URL (for per-PR show/hide toggle)
const prAnnotationsByUrl = new Map();

// Module-level flag: controls whether graphic/weasis annotations are applied.
let _annotationsEnabled = true;

/** Enable or disable PR graphic annotation rendering (does not affect windowing / zoom). */
export const setPRAnnotationsEnabled = (enabled) => {
  _annotationsEnabled = !!enabled;
};

const trackAnnotation = (imageId, uid, prUrl = null) => {
  if (!prAnnotationsByImageId.has(imageId)) prAnnotationsByImageId.set(imageId, new Set());
  prAnnotationsByImageId.get(imageId).add(uid);

  if (prUrl) {
    if (!prAnnotationsByUrl.has(prUrl)) prAnnotationsByUrl.set(prUrl, new Set());
    prAnnotationsByUrl.get(prUrl).add(uid);
  }
};

/**
 * Register a PR annotation in Cornerstone's locking set so it is truly
 * NON-INTERACTIVE. Cornerstone's hit-testing/move code checks the locking
 * registry (`isAnnotationLocked`), NOT the annotation's own `isLocked` field —
 * so without this a "locked" PR annotation is still draggable, and moving it
 * fires a stats recompute that makes it vanish or multiply (bug 16). Must be
 * called AFTER the annotation is in the manager (locking reads it from state).
 */
const lockPRAnnotation = (uid) => {
  try { annotation.locking.setAnnotationLocked(uid, true); } catch (_) {}
};

/** Returns all currently cached PR data objects (one per parsed PR file). */
export const getAllCachedPRData = () => [...prCache.values()];

/** Returns { url, prData } pairs for every cached PR — used to build the PR list panel. */
export const getAllCachedPREntries = () =>
  [...prCache.entries()].map(([url, data]) => ({ url, data }));

/** Returns the cached prData for a single URL, or undefined if not yet parsed. */
export const getCachedPRData = (url) => prCache.get(url);

/**
 * Returns the set of imageIds that currently have PR annotations applied.
 */
export const getPRAnnotatedImageIds = () => new Set(prAnnotationsByImageId.keys());

/**
 * True once a PR's annotations are in the manager. A multi-frame PR places its
 * objects on their own frames (not the frame on screen), so "is this imageId
 * annotated" no longer tells whether the PR still needs placing.
 */
export const isPRPlaced = (prUrl) => !!prUrl && (prAnnotationsByUrl.get(prUrl)?.size ?? 0) > 0;

/** Remove every PR-created annotation from the annotation manager and clear tracking maps. */
export const removeAllPRAnnotations = (viewportRenderCallback) => {
  const mgr = annotation.state.getAnnotationManager();
  for (const uids of prAnnotationsByImageId.values()) {
    uids.forEach((uid) => {
      try { annotation.locking.setAnnotationLocked(uid, false); } catch (_) {}
      try { mgr.removeAnnotation(uid); } catch (_) {}
    });
  }
  prAnnotationsByImageId.clear();
  prAnnotationsByUrl.clear();
  viewportRenderCallback?.();
};

/**
 * Remove annotations that belong to a single PR (identified by its URL).
 * Used when the user unchecks a PR in the list panel.
 */
export const removePRAnnotationsByUrl = (url, viewportRenderCallback) => {
  const mgr = annotation.state.getAnnotationManager();
  const uids = prAnnotationsByUrl.get(url) ?? new Set();
  uids.forEach((uid) => {
    try { annotation.locking.setAnnotationLocked(uid, false); } catch (_) {}
    try { mgr.removeAnnotation(uid); } catch (_) {}
  });
  prAnnotationsByUrl.delete(url);

  // Also scrub from the imageId map so getPRAnnotatedImageIds stays accurate.
  for (const [imageId, uidSet] of prAnnotationsByImageId.entries()) {
    for (const uid of uids) uidSet.delete(uid);
    if (uidSet.size === 0) prAnnotationsByImageId.delete(imageId);
  }
  viewportRenderCallback?.();
};

/**
 * Download and parse a PR DICOM file from the given URL.
 * Results are cached by URL.
 */
export const parsePRFile = async (url) => {
  if (prCache.has(url)) return prCache.get(url);

  const res = await axiosInterceptor.get(url, { responseType: "blob" });

  const arrayBuffer = await res.data.arrayBuffer();
  const byteArray = new Uint8Array(arrayBuffer);
  const dataSet = dicomParser.parseDicom(byteArray);
  const prData = await extractPRData(dataSet);
  prCache.set(url, prData);
  return prData;
};

/**
 * Decompress bytes using the browser's DecompressionStream.
 */
const decompress = async (bytes, format) => {
  const stream = new DecompressionStream(format);
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();

  writer.write(bytes);
  writer.close();

  const chunks = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return new TextDecoder("utf-8").decode(result);
};

/**
 * Strip the single DICOM even-length padding byte (0x00) if length is odd after stripping.
 * Only removes one byte — gzip data can legitimately end in 0x00.
 */
const stripDicomPadding = (bytes) => {
  if (bytes.length % 2 !== 0) return bytes; // odd length — no padding added
  if (bytes[bytes.length - 1] === 0x00) return bytes.slice(0, bytes.length - 1);
  return bytes;
};

/**
 * Try decompressing with gzip (stripping DICOM padding first), then deflate-raw.
 */
const decompressWeasis = async (compressedBytes) => {
  const stripped = stripDicomPadding(compressedBytes);

  // Try gzip (Weasis default)
  try {
    return await decompress(stripped, "gzip");
  } catch (_) {}

  // Fallback: raw deflate (no gzip header)
  try {
    return await decompress(stripped, "deflate-raw");
  } catch (_) {}

  // Fallback: zlib-wrapped deflate
  return await decompress(stripped, "deflate");
};

/**
 * Extract the Weasis private tag (7107,7001), decompress and parse XML.
 * Returns an array of Weasis graphic objects, or [] if tag absent.
 */
const extractWeasisAnnotations = async (dataSet) => {
  const elem = dataSet.elements["x71077001"];
  if (!elem) return [];

  // Use .slice() so the offset is relative to the Uint8Array, not its underlying ArrayBuffer
  const compressedBytes = dataSet.byteArray.slice(elem.dataOffset, elem.dataOffset + elem.length);

  let xmlString;
  try {
    xmlString = await decompressWeasis(compressedBytes);
  } catch (err) {
    console.warn("PR: Failed to decompress Weasis private tag:", err);
    return [];
  }

  console.log("[Weasis PR XML]", xmlString); // Inspect raw format in devtools

  return parseWeasisXML(xmlString);
};

/**
 * Parse Weasis presentation state XML into a normalised list of graphic objects.
 *
 * Weasis XML format (confirmed from wire):
 *   <graphics>
 *     <polyline ...>           ← tag name IS the graphic type
 *       <paint rgb="AARRGGBB"/>
 *       <graphicLabel><labels><label>Length : 121.2 mm</label></labels></graphicLabel>
 *       <pts><pt x="..." y="..."/>...</pts>
 *     </polyline>
 *     <line ...> ... </line>
 *     <ellipse ...> ... </ellipse>
 *     ...
 *   </graphics>
 */
const parseWeasisXML = (xmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");

  const graphicsEl = doc.querySelector("graphics");
  if (!graphicsEl) return [];

  const graphics = [];

  Array.from(graphicsEl.children).forEach((el) => {
    const type = el.tagName.toUpperCase();

    // Label from <graphicLabel><labels><label>
    const labelEl = el.querySelector("graphicLabel labels label");
    const label = labelEl?.textContent?.trim() ?? "";

    // Color from <paint rgb="AARRGGBB"> (Weasis stores Java ARGB as 8-char hex)
    const paintEl = el.querySelector("paint");
    const rgbHex = paintEl?.getAttribute("rgb") ?? "";
    // Drop the alpha prefix (first 2 chars) to get CSS #RRGGBB
    const color = rgbHex.length === 8 ? `#${rgbHex.slice(2)}` : "#ffff00";

    // Points from <pts><pt x="..." y="..."/>
    const points = [];
    el.querySelectorAll("pts pt").forEach((pt) => {
      const x = parseFloat(pt.getAttribute("x"));
      const y = parseFloat(pt.getAttribute("y"));
      if (!isNaN(x) && !isNaN(y)) points.push({ x, y });
    });

    if (points.length) {
      graphics.push({ type, points, label, color });
    }
  });

  return graphics;
};

// ─── PR measurement-type resolution (Phase 1: decode by type, not geometry) ───
//
// The canonical measurement type lives in (0070,0068) Graphic Layer Description
// and, for arrows, (0070,0294) Compound Graphic Type — NEVER in the point count.
// See GSPS_INTEROP_DIAGNOSIS.md Findings 1, 3, 9.

const PR_KIND = {
  LENGTH: "LENGTH", ANGLE: "ANGLE", COBB: "COBB", ARROW: "ARROW",
  PROBE: "PROBE", PENCIL: "PENCIL", RECTANGLE: "RECTANGLE",
  ELLIPSE: "ELLIPSE", CIRCLE: "CIRCLE", TEXT: "TEXT", POLYLINE: "POLYLINE",
  BIDIRECTIONAL: "BIDIRECTIONAL", FLEXPOLY: "FLEXPOLY",
};

/**
 * Resolve the measurement kind from the canonical DICOM type carriers.
 * Authority order:
 *   1. (0070,0294) Compound Graphic Type   — e.g. "ARROW"
 *   2. (0070,0068) Graphic Layer Description — "Measurement <kind>…"
 *   3. Fallback: geometry (graphic type + point count) — ONLY for legacy /
 *      Weasis / Augmento-origin objects that carry no type marker yet.
 */
/**
 * Measurement kind named by a (0070,0068) Graphic Layer Description, or null
 * when the description names no known kind (e.g. Us2.ai's generic "Measurements").
 */
const kindFromDescription = (prType, numPoints) => {
  const d = (prType || "").trim().toLowerCase();
  if (!d) return null;
  if (d.includes("arrow"))     return PR_KIND.ARROW;
  if (d.includes("cobb"))      return PR_KIND.COBB;
  if (d.includes("rectangle")) return PR_KIND.RECTANGLE;
  if (d.includes("pencil") || d.includes("freehand")) return PR_KIND.PENCIL;
  if (d.includes("ellipse") || d.includes("oval")) return PR_KIND.ELLIPSE;
  if (d.includes("circle"))    return PR_KIND.CIRCLE;
  if (d.includes("probe") || d.includes("point")) return PR_KIND.PROBE;
  if (d.includes("angle"))     return PR_KIND.ANGLE;
  if (d.includes("text"))      return PR_KIND.TEXT;
  if (d.includes("flexpoly"))  return PR_KIND.FLEXPOLY;
  if (d.includes("bidirectional")) return PR_KIND.BIDIRECTIONAL;
  if (d.includes("polyline") || d.includes("length"))
    return numPoints === 2 ? PR_KIND.LENGTH : PR_KIND.POLYLINE;
  return null;
};

const resolvePRKind = ({ prType, compoundType, graphicType, numPoints }) => {
  const ct = (compoundType || "").trim().toUpperCase();
  if (ct === "ARROW") return PR_KIND.ARROW;

  const described = kindFromDescription(prType, numPoints);
  if (described) return described;

  // ── Fallback: legacy geometry inference (no (0070,0068)/(0070,0294)) ──
  const gt = (graphicType || "").toUpperCase();
  if (gt === "TEXT")     return PR_KIND.TEXT;
  if (gt === "POINT")    return PR_KIND.PROBE;
  if (gt === "CIRCLE")   return PR_KIND.CIRCLE;
  if (gt === "ELLIPSE")  return PR_KIND.ELLIPSE;
  if (gt === "POLYLINE") {
    if (numPoints === 2) return PR_KIND.LENGTH;
    if (numPoints === 3) return PR_KIND.ANGLE;
    return PR_KIND.POLYLINE;
  }
  return PR_KIND.POLYLINE;
};

/** First signed decimal in a string, or null. ("735.07 mm" → 735.07, "10.35°" → 10.35) */
const parseLeadingNumber = (s) => {
  if (s == null) return null;
  const m = String(s).match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

/** Trailing unit token in a value string, or "". ("735.07 mm" → "mm") */
const parseTrailingUnit = (s) => {
  if (!s) return "";
  const m = String(s).trim().match(/([a-zA-Z²°]+)\s*$/);
  return m ? m[1] : "";
};

/**
 * Read a (0070,0022)-style FL Graphic Data element as a flat [col,row,…] array.
 * Graphic Data is binary 32-bit floats (VR=FL); .slice() copies into a fresh,
 * 4-byte-aligned buffer required by Float32Array.
 */
const readGraphicFloats = (ds, tagHex) => {
  const elem = ds.elements[tagHex];
  if (!elem) return [];
  const bytes = ds.byteArray.slice(elem.dataOffset, elem.dataOffset + elem.length);
  return Array.from(new Float32Array(bytes.buffer, 0, Math.floor(bytes.length / 4)));
};

/**
 * DICOM CIELab triplet (US×3, PS3.3 C.10.7.1.1 — L 0..100, a/b −128..127 scaled
 * to 0..0xFFFF) → "rgb(r, g, b)" (D65, sRGB). Null when the triplet is absent.
 */
export const cielabToRgb = (lab) => {
  if (!Array.isArray(lab) || lab.length < 3 || lab.some((v) => !Number.isFinite(v))) return null;
  const L = (lab[0] / 65535) * 100;
  const a = (lab[1] / 65535) * 255 - 128;
  const b = (lab[2] / 65535) * 255 - 128;
  const fy = (L + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;
  const finv = (t) => (t ** 3 > 0.008856 ? t ** 3 : (t - 16 / 116) / 7.787);
  const X = 0.95047 * finv(fx);
  const Y = finv(fy);
  const Z = 1.08883 * finv(fz);
  const lin = [
    3.2406 * X - 1.5372 * Y - 0.4986 * Z,
    -0.9689 * X + 1.8758 * Y + 0.0415 * Z,
    0.0557 * X - 0.204 * Y + 1.057 * Z,
  ];
  const [r, g, bl] = lin.map((u) => {
    const c = u > 0.0031308 ? 1.055 * u ** (1 / 2.4) - 0.055 : 12.92 * u;
    return Math.round(255 * Math.min(1, Math.max(0, c)));
  });
  return `rgb(${r}, ${g}, ${bl})`;
};

/** CIELab colour (US×3 at `tag`) of the first item of style sequence `seqTag`, as rgb(). */
const readStyleColor = (ds, seqTag, tag) => {
  const style = ds?.elements?.[seqTag]?.items?.[0]?.dataSet;
  if (!style?.elements?.[tag]) return null;
  return cielabToRgb([0, 1, 2].map((i) => style.uint16(tag, i)));
};

/**
 * Read a Referenced Image Sequence (0008,1140) item as { sop, frames }.
 * frames = 1-based (0008,1160) Referenced Frame Numbers (IS, multi-valued);
 * empty means the reference covers EVERY frame of the image (PS3.3 C.11.11).
 */
const readImageReference = (refItemDs) => {
  const sop = refItemDs?.string("x00081155")?.trim();
  if (!sop) return null;
  const frames = (refItemDs.string("x00081160") ?? "")
    .split("\\")
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  return { sop, frames };
};

const extractPRData = async (dataSet) => {
  const pr = {
    // PR's own SOP Instance UID (0008,0018) — used to derive STABLE annotation
    // UIDs so re-applying the same PR never creates duplicates (bugs 4, 16).
    sopInstanceUID:            dataSet.string("x00080018")?.trim() ?? "",
    referencedSOPInstanceUIDs: [],
    // Top-level image references with their multi-frame frame numbers:
    // [{ sop, frames }] — frames [] = all frames of that image.
    referencedImages:          [],
    voiLUT: null,
    rotation: 0,
    horizontalFlip: false,
    displayedArea: null,       // from Displayed Area Selection Sequence (0070,005A)
    graphicAnnotations: [],    // from standard DICOM graphic annotation sequence
    spineLabels: [],           // spine-labelling items: { vertebra, col, row, sopInstanceUID }
    weasisAnnotations: [],     // from Weasis private gzip tag
    // ── Presentation State identification metadata ────────────────────────────
    contentLabel:              dataSet.string("x00700080")?.trim() ?? "",
    contentDescription:        dataSet.string("x00700081")?.trim() ?? "",
    presentationCreationDate:  dataSet.string("x00700082")?.trim() ?? "",
    presentationCreationTime:  dataSet.string("x00700083")?.trim() ?? "",
    contentCreatorName:        dataSet.string("x00700084")?.trim() ?? "",
    manufacturer:              dataSet.string("x00080070")?.trim() ?? "",
    manufacturerModelName:     dataSet.string("x00081090")?.trim() ?? "",
  };

  // Referenced Series Sequence (0008,1115) → Referenced Image Sequence (0008,1140) → SOP Instance UID (0008,1155)
  const refSeriesSeq = dataSet.elements["x00081115"];
  if (refSeriesSeq?.items) {
    refSeriesSeq.items.forEach((seriesItem) => {
      const refImageSeq = seriesItem.dataSet.elements["x00081140"];
      if (refImageSeq?.items) {
        refImageSeq.items.forEach((imgItem) => {
          const ref = readImageReference(imgItem.dataSet);
          if (!ref) return;
          pr.referencedSOPInstanceUIDs.push(ref.sop);
          pr.referencedImages.push(ref);
        });
      }
    });
  }

  // VOI LUT: check Softcopy VOI LUT Sequence (0028,3110) first (standard GSPS location),
  // then fall back to root-level Window Center/Width (0028,1050)/(0028,1051).
  let wcStr = null;
  let wwStr = null;
  const softcopyVOISeq = dataSet.elements["x00283110"];
  if (softcopyVOISeq?.items?.length) {
    wcStr = softcopyVOISeq.items[0].dataSet.string("x00281050");
    wwStr = softcopyVOISeq.items[0].dataSet.string("x00281051");
  }
  if (!wcStr || !wwStr) {
    wcStr = dataSet.string("x00281050");
    wwStr = dataSet.string("x00281051");
  }
  if (wcStr && wwStr) {
    pr.voiLUT = {
      windowCenter: parseFloat(wcStr.split("\\")[0]),
      windowWidth: parseFloat(wwStr.split("\\")[0]),
    };
  }

  // Spatial Transformation: Rotation (0070,0042), Flip (0070,0041)
  const rotationStr = dataSet.string("x00700042");
  if (rotationStr) pr.rotation = parseInt(rotationStr, 10) || 0;
  pr.horizontalFlip = dataSet.string("x00700041") === "YES";

  // Displayed Area Selection Sequence (0070,005A) — pan/zoom region
  // Top Left (0070,0052) and Bottom Right (0070,0053) are SL (signed 32-bit int) multi-values.
  const displayedAreaSeq = dataSet.elements["x0070005a"];
  if (displayedAreaSeq?.items?.length) {
    const daDs = displayedAreaSeq.items[0].dataSet;
    const tlElem = daDs.elements["x00700052"];
    const brElem = daDs.elements["x00700053"];
    if (tlElem && brElem) {
      const tlBytes = daDs.byteArray.slice(tlElem.dataOffset, tlElem.dataOffset + tlElem.length);
      const brBytes = daDs.byteArray.slice(brElem.dataOffset, brElem.dataOffset + brElem.length);
      const tlArr = new Int32Array(tlBytes.buffer, 0, tlBytes.length / 4);
      const brArr = new Int32Array(brBytes.buffer, 0, brBytes.length / 4);

      // Presentation Pixel Magnification Ratio (0070,0103) FL — present when the
      // PR carries a genuine zoom factor via MAGNIFY mode (Finding 6).
      let magnificationRatio = null;
      const magElem = daDs.elements["x00700103"];
      if (magElem && magElem.length >= 4) {
        const mb = daDs.byteArray.slice(magElem.dataOffset, magElem.dataOffset + 4);
        magnificationRatio = new Float32Array(mb.buffer, 0, 1)[0];
      }

      pr.displayedArea = {
        // Image-pixel corners (MedDream/Augmento ≥Phase 4). Legacy Augmento PRs
        // may carry display/overscan corners with negatives — applyDisplayedArea
        // handles both.
        topLeft:     [tlArr[0], tlArr[1]],
        bottomRight: [brArr[0], brArr[1]],
        presentationSizeMode: daDs.string("x00700100")?.trim() ?? "SCALE TO FIT",
        magnificationRatio,
      };
    }
  }

  // Graphic Layer Sequence (0070,0060): map layer name (0070,0002) → the
  // canonical measurement type carried in (0070,0068) Graphic Layer Description.
  const layerDescByName = {};
  const layerSeq = dataSet.elements["x00700060"];
  if (layerSeq?.items) {
    layerSeq.items.forEach((layerItem) => {
      const name = layerItem.dataSet.string("x00700002")?.trim();
      const desc = layerItem.dataSet.string("x00700068")?.trim();
      if (name && desc) layerDescByName[name] = desc;
    });
  }

  // Standard DICOM Graphic Annotations (0070,0001)
  const graphicAnnotationSeq = dataSet.elements["x00700001"];
  if (graphicAnnotationSeq?.items) {
    graphicAnnotationSeq.items.forEach((annotItem, groupId) => {
      // Per-annotation Referenced Image Sequence (0008,1140) → SOP Instance UID (0008,1155).
      // Some backends store image references here instead of the top-level (0008,1115) sequence.
      const annotRefImageSeq = annotItem.dataSet.elements["x00081140"];
      // This item's own image (+ frame) references. Empty → the item applies to
      // the PR's top-level referenced images (pr.referencedImages).
      const itemRefs = [];
      if (annotRefImageSeq?.items) {
        annotRefImageSeq.items.forEach((refItem) => {
          const ref = readImageReference(refItem.dataSet);
          if (!ref) return;
          itemRefs.push(ref);
          if (!pr.referencedSOPInstanceUIDs.includes(ref.sop)) {
            pr.referencedSOPInstanceUIDs.push(ref.sop);
          }
        });
      }
      const itemStart = pr.graphicAnnotations.length;

      // Canonical measurement type for this annotation item (Finding 1):
      // resolve via this item's layer name (0070,0002) → (0070,0068). May be
      // absent for legacy / Augmento-origin objects (those fall back to geometry).
      const layerName = annotItem.dataSet.string("x00700002")?.trim();
      const prType = layerName ? (layerDescByName[layerName] ?? "") : "";

      // Text label from Text Object Sequence (0070,0008) → Unformatted Text Value (0070,0006).
      // This is the STORED measurement value (Finding 2) — display it, never recompute.
      let label = "";
      const textObjectSeq = annotItem.dataSet.elements["x00700008"];
      if (textObjectSeq?.items?.length) {
        label = textObjectSeq.items[0].dataSet.string("x00700006")?.trim() ?? "";
      }

      // Compound Graphic Sequence (0070,0209): for arrows the Compound Graphic
      // Type (0070,0294) wins and the (0070,0009) polyline is a duplicate
      // fallback — render the compound, ignore the polyline (Findings 3, 4).
      const compoundSeq = annotItem.dataSet.elements["x00700209"];
      let compoundType = "";
      let compoundCoords = [];
      if (compoundSeq?.items?.length) {
        const cItem = compoundSeq.items[0].dataSet;
        compoundType = cItem.string("x00700294")?.trim() ?? "";
        compoundCoords = readGraphicFloats(cItem, "x00700022");
      }

      const graphicObjectSeq = annotItem.dataSet.elements["x00700009"];

      // ── Spine-labelling item ────────────────────────────────────────────────
      // Layer description "Measurement spine" (0070,0068) marks a spine-label item:
      // each Text Object (0070,0008) is a vertebra name (C1, T1, …) paired with a
      // POINT graphic object (0070,0009). Collect them into pr.spineLabels so the
      // viewer restores them into the native spine overlay (NOT as measurements),
      // then skip the generic graphic handling for this item. Works for both
      // Augmento- and MedDream-authored spine PRs (both use this layer description).
      if (/spine/i.test(prType)) {
        // Referenced image for THIS item (labels can span multiple images).
        const itemRefSop = itemRefs[0]?.sop ?? pr.referencedSOPInstanceUIDs[0] ?? null;
        const itemRefFrame = itemRefs[0]?.frames?.[0] ?? null;
        const textItems = textObjectSeq?.items ?? [];
        const goItems   = graphicObjectSeq?.items ?? [];
        textItems.forEach((ti, i) => {
          const vertebra = ti.dataSet.string("x00700006")?.trim();
          if (!vertebra) return;
          // Position: prefer the POINT graphic (0070,0022), then the text Anchor
          // Point (0070,0014), then the bounding-box top-left (0070,0010).
          let col = null, row = null;
          const goPt = goItems[i] ? readGraphicFloats(goItems[i].dataSet, "x00700022") : [];
          if (goPt.length >= 2) { [col, row] = goPt; }
          else {
            const ap = readGraphicFloats(ti.dataSet, "x00700014");
            if (ap.length >= 2) { [col, row] = ap; }
            else {
              const tl = readGraphicFloats(ti.dataSet, "x00700010");
              if (tl.length >= 2) { [col, row] = tl; }
            }
          }
          if (col == null || row == null) return;
          pr.spineLabels.push({ vertebra, col, row, sopInstanceUID: itemRefSop, frameNumber: itemRefFrame });
        });
        return; // spine items are not rendered as generic graphic annotations
      }

      // ── Annotation set (vendor "structured" layout, e.g. Us2.ai) ─────────────
      // ONE item holding many text objects + many graphic objects under a layer
      // description that names no measurement kind ("Measurements"): the shapes
      // are display-only geometry (method-of-disks lines, contours, Doppler peak
      // points) and the VALUES live in the texts — a numbered list at a fixed
      // spot ("(1) RA area A4C (s): 12.7 cm^2") plus a "+1" marker beside each
      // shape. MedDream simply draws every object where it is stored, so do the
      // same: each shape plain (no measurement tool, no recomputed value) in its
      // own line colour, each text verbatim at its anchor / bounding box. Items
      // with a known kind (MedDream / Augmento) never take this path.
      const textCount = textObjectSeq?.items?.length ?? 0;
      const graphicCount = graphicObjectSeq?.items?.length ?? 0;
      const isAnnotationSet =
        !compoundType &&
        !kindFromDescription(prType, 0) &&
        (textCount > 1 || graphicCount > 1);

      const isCompoundEllipse = compoundType.toUpperCase() === "ELLIPSE" || compoundType.toUpperCase() === "OVAL";
      if (isAnnotationSet) {
        graphicObjectSeq?.items?.forEach((objItem) => {
          const type = objItem.dataSet.string("x00700023")?.trim();
          const numPoints = objItem.dataSet.uint16("x00700021") ?? 0;
          const coords = readGraphicFloats(objItem.dataSet, "x00700022");
          if (!type || !coords.length) return;
          pr.graphicAnnotations.push({
            type, numPoints, coords, label: "",
            prType, compoundType: "", groupId, groupLead: true,
            plain: true,
            // (0070,0232) Line Style → (0070,0251) Pattern On Color CIELab Value.
            color: readStyleColor(objItem.dataSet, "x00700232", "x00700251"),
          });
        });
        // Copy-button text per object: a list line "(2) LVIDd: 43.9 mm" copies
        // "LVIDd: 43.9 mm"; its marker "+2" copies the same measurement.
        const allTexts = (textObjectSeq?.items ?? [])
          .map((t) => t.dataSet.string("x00700006")?.trim() ?? "");
        const listByNumber = {};
        allTexts.forEach((t) => {
          const m = t.match(/^\((\d+)\)\s*(.+)$/);
          if (m) listByNumber[m[1]] = m[2];
        });
        const copyTextFor = (t) => {
          const line = t.match(/^\((\d+)\)\s*(.+)$/);
          if (line) return line[2];
          const marker = t.match(/^\+(\d+)$/);
          if (marker) return listByNumber[marker[1]] ?? null;
          return t;
        };
        textObjectSeq?.items?.forEach((textItem) => {
          const text = textItem.dataSet.string("x00700006")?.trim() ?? "";
          if (!text) return;
          // Anchor Point (0070,0014) when present, else the bounding box's top-left
          // (0070,0010) — the list lines and "+n" markers carry only a box.
          let pos = readGraphicFloats(textItem.dataSet, "x00700014");
          if (pos.length < 2) pos = readGraphicFloats(textItem.dataSet, "x00700010");
          if (pos.length < 2) return;
          // Bounding box (0070,0010)/(0070,0011): its height in IMAGE pixels is the
          // line height the author laid the text out at — used to scale the font
          // with zoom so the list rows never overlap (MedDream does the same).
          const tl = readGraphicFloats(textItem.dataSet, "x00700010");
          const br = readGraphicFloats(textItem.dataSet, "x00700011");
          const boxBottomLeft = tl.length >= 2 && br.length >= 2 && br[1] > tl[1]
            ? [pos[0], pos[1] + (br[1] - tl[1])]
            : null;
          pr.graphicAnnotations.push({
            boxBottomLeft,
            copyText: copyTextFor(text),
            type: "TEXT", numPoints: 1, coords: [pos[0], pos[1]], label: text,
            prType: "Measurement text", compoundType: "", groupId, groupLead: true,
            plain: true,
            // (0070,0231) Text Style → (0070,0241) Text Color CIELab Value.
            color: readStyleColor(textItem.dataSet, "x00700231", "x00700241"),
            // A text tied to a measurement by Tracking ID (0062,0020) is the "+n"
            // marker beside its shape — MedDream draws it in a dark box; the
            // numbered list lines carry no Tracking ID and stay unboxed.
            boxed: !!textItem.dataSet.string("x00620020")?.trim(),
          });
        });
      } else if (compoundType && compoundCoords.length && !isCompoundEllipse) {
        // Compound graphic present (e.g. ARROW) — emit one object from the
        // compound geometry and skip the polyline fallback to avoid doubles.
        // Exception: ELLIPSE — MedDream stores a low-fidelity 2-point compound
        // alongside the full 4-point ELLIPSE in (0070,0009). Always use the real
        // graphic object for ellipses (handled in the graphicObjectSeq branch below).
        // An arrow NEVER carries text: MedDream stores arrow and text as separate
        // items (meddream_bug06), so render the arrow text-less. If this item does
        // carry a text value, emit it as a SEPARATE plain TEXT annotation using the
        // DICOM text anchor (0070,0014) when available, falling back to head+offset.
        pr.graphicAnnotations.push({
          type: "POLYLINE",
          numPoints: compoundCoords.length / 2,
          coords: compoundCoords,
          label: "",
          prType, compoundType, groupId, groupLead: true,
        });
        if (label) {
          // Use the DICOM anchor point (0070,0014) from the text object for accurate
          // placement, falling back to compound head + small offset.
          let tx, ty;
          const textItems = textObjectSeq?.items;
          if (textItems?.length) {
            const apElem = textItems[0].dataSet.elements["x00700014"];
            if (apElem) {
              const apBytes = textItems[0].dataSet.byteArray.slice(apElem.dataOffset, apElem.dataOffset + apElem.length);
              const apFloat = new Float32Array(apBytes.buffer, 0, 2);
              tx = apFloat[0]; ty = apFloat[1];
            }
          }
          if (tx == null) {
            tx = (compoundCoords[compoundCoords.length - 2] ?? compoundCoords[0]) + 10;
            ty = (compoundCoords[compoundCoords.length - 1] ?? compoundCoords[1]) + 10;
          }
          pr.graphicAnnotations.push({
            type: "TEXT", numPoints: 1, coords: [tx, ty], label,
            prType: "Measurement text", compoundType: "", groupId, groupLead: false,
          });
        }
      } else if (graphicObjectSeq?.items?.length && (prType || "").toLowerCase().includes("bidirectional")) {
        // Bidirectional (FlexPoly): two 2-point POLYLINEs (long axis + short axis).
        // Collect into one 4-point entry so the apply path can reconstruct the
        // native BidirectionalTool from [p0,p1,p2,p3].
        const biArms = [];
        graphicObjectSeq.items.forEach((objItem) => {
          const c = readGraphicFloats(objItem.dataSet, "x00700022");
          if (c.length >= 4) biArms.push(c[0], c[1], c[2], c[3]);
        });
        if (biArms.length >= 8) {
          pr.graphicAnnotations.push({
            type: "POLYLINE", numPoints: 4, coords: biArms.slice(0, 8), label,
            prType, compoundType: "", groupId, groupLead: true,
          });
        } else if (biArms.length >= 4) {
          pr.graphicAnnotations.push({
            type: "POLYLINE", numPoints: 2, coords: biArms.slice(0, 4), label,
            prType, compoundType: "", groupId, groupLead: true,
          });
        }
      } else if (graphicObjectSeq?.items?.length && (prType || "").toLowerCase().includes("cobb")) {
        // Cobb angle: MedDream stores it as TWO solid 2-point arms (+ an optional
        // DASHED link line, which the native CobbAngle tool draws itself). Collect
        // just the two solid arms into ONE 4-point entry so the apply path rebuilds
        // a NATIVE CobbAngleTool — solid arms + dotted link line + angle text,
        // identical to a Cobb drawn in Basic. (Previously each segment became a
        // separate measuring line + an arrow-text label = the reported bug 14.)
        const arms = [];
        graphicObjectSeq.items.forEach((objItem) => {
          if ((objItem.dataSet.uint16("x00700021") ?? 0) !== 2) return; // arms are 2-point
          const ls = objItem.dataSet.elements["x00700232"];
          const dash = ls?.items?.[0]?.dataSet.string("x00700254")?.trim().toUpperCase();
          if (dash === "DASHED") return;                                // skip the link line
          const c = readGraphicFloats(objItem.dataSet, "x00700022");
          if (c.length >= 4) arms.push(c[0], c[1], c[2], c[3]);
        });
        if (arms.length >= 8) {
          pr.graphicAnnotations.push({
            type: "POLYLINE", numPoints: 4, coords: arms.slice(0, 8), label,
            prType, compoundType: "", groupId, groupLead: true,
          });
        } else {
          // Degenerate (fewer than two solid arms) — fall back to raw segments.
          graphicObjectSeq.items.forEach((objItem, objIdx) => {
            const type = objItem.dataSet.string("x00700023");
            const numPoints = objItem.dataSet.uint16("x00700021") ?? 0;
            const coords = readGraphicFloats(objItem.dataSet, "x00700022");
            if (type && coords.length) {
              pr.graphicAnnotations.push({
                type, numPoints, coords, label,
                prType, compoundType: "", groupId, groupLead: objIdx === 0,
              });
            }
          });
        }
      } else if (graphicObjectSeq?.items?.length || isCompoundEllipse) {
        // For compound ELLIPSE: use the real 4-point ELLIPSE from (0070,0009) graphic object seq,
        // not the 2-point compound shorthand from (0070,0209). Falls here when isCompoundEllipse.
        graphicObjectSeq?.items?.forEach((objItem, objIdx) => {
          const type = objItem.dataSet.string("x00700023");
          // (0070,0021) Number of Graphic Points has VR=US — must use uint16(), not string()
          const numPoints = objItem.dataSet.uint16("x00700021") ?? 0;
          const coords = readGraphicFloats(objItem.dataSet, "x00700022");

          if (type && coords.length) {
            const isEllipse = type.toUpperCase() === "ELLIPSE";
            // For ellipse/oval, the native EllipticalROITool recomputes and renders
            // its OWN stats (Area / Mean / Max / Std Dev, stacked) on load — same as a
            // Basic-viewer-drawn ellipse. So emit ONLY the shape (label-less) and do
            // NOT add a separate TEXT annotation: doing so duplicated the value and
            // showed the stored stats string on a single line. Non-ellipse tools keep
            // their label on the shape's own textbox.
            pr.graphicAnnotations.push({
              type, numPoints, coords,
              label: isEllipse ? "" : label,
              prType, compoundType: "", groupId, groupLead: objIdx === 0,
            });
          }
        });
      } else if (textObjectSeq?.items?.length) {
        // Text-only annotation — no graphic object, render as a labelled probe point.
        textObjectSeq.items.forEach((textItem) => {
          const text = textItem.dataSet.string("x00700006")?.trim() ?? "";
          if (!text) return;

          // Position: prefer Anchor Point (0070,0014) FL[2] (image pixel coords),
          // then bounding-box top-left (0070,0010) (also image pixel coords in MedDream).
          let col = 0, row = 0;
          const apElem = textItem.dataSet.elements["x00700014"];
          if (apElem) {
            const apBytes = textItem.dataSet.byteArray.slice(apElem.dataOffset, apElem.dataOffset + apElem.length);
            const apFloat = new Float32Array(apBytes.buffer, 0, 2);
            col = apFloat[0];
            row = apFloat[1];
          } else {
            const tlElem = textItem.dataSet.elements["x00700010"];
            if (tlElem) {
              const tlBytes = textItem.dataSet.byteArray.slice(tlElem.dataOffset, tlElem.dataOffset + tlElem.length);
              const tlFloat = new Float32Array(tlBytes.buffer, 0, 2);
              col = tlFloat[0];
              row = tlFloat[1];
            }
          }
          // Force prType to include "text" — a text-only annotation is always
          // PR_KIND.TEXT regardless of whether (0070,0068) was set. Without this,
          // a standalone Advance-viewer text with no layer desc falls through
          // to PR_KIND.POLYLINE in the geometry fallback and never renders.
          pr.graphicAnnotations.push({
            type: "TEXT", numPoints: 1, coords: [col, row], label: text,
            prType: prType || "Measurement text", compoundType: "", groupId, groupLead: true,
          });
        });
      }

      // Scope every object emitted for this item to the item's own image/frame.
      for (let i = itemStart; i < pr.graphicAnnotations.length; i++) {
        pr.graphicAnnotations[i].refs = itemRefs;
      }
    });
  }

  // Weasis private tag (7107,7001) — gzip-compressed XML
  pr.weasisAnnotations = await extractWeasisAnnotations(dataSet);

  return pr;
};

// ─── Coordinate conversion ────────────────────────────────────────────────────

/**
 * Convert DICOM image pixel [col, row] → CS3D world [x, y, z] using the
 * image plane metadata (imagePositionPatient + cosines + pixelSpacing).
 * Falls back to [col, row, 0] if metadata is unavailable.
 */
const pixelToWorld = (imageId, col, row) => {
  const meta = cornerstone.metaData.get("imagePlaneModule", imageId);
  if (
    meta?.imagePositionPatient &&
    meta?.rowCosines &&
    meta?.columnCosines &&
    meta?.rowPixelSpacing != null &&
    meta?.columnPixelSpacing != null
  ) {
    const { imagePositionPatient: ipp, rowCosines: rc, columnCosines: cc, rowPixelSpacing: rps, columnPixelSpacing: cps } = meta;
    // DICOM standard: P = IPP + col * colSpacing * rowCosines + row * rowSpacing * columnCosines
    return [
      ipp[0] + col * cps * rc[0] + row * rps * cc[0],
      ipp[1] + col * cps * rc[1] + row * rps * cc[1],
      ipp[2] + col * cps * rc[2] + row * rps * cc[2],
    ];
  }
  // Fallback for images WITHOUT patient-space geometry (CR/DX/SC — no
  // ImagePositionPatient/ImageOrientationPatient). Cornerstone's StackViewport
  // still renders such an image in world space scaled by pixel spacing (origin
  // [0,0,0], identity orientation), so map pixel→world the SAME way: world =
  // pixelIndex × spacing. Treating pixels as raw world units (the old behaviour)
  // placed annotations far outside the spacing-scaled image, so they vanished.
  const meta2 = meta ?? {};
  const fps = meta2.rowPixelSpacing ?? 1;
  const fcps = meta2.columnPixelSpacing ?? 1;
  return [col * fcps, row * fps, 0];
};

/**
 * Inverse of pixelToWorld: CS3D world [x, y, z] → DICOM pixel [col, row].
 * Solves: P = IPP + col*cps*rc + row*rps*cc  →  col = dot(P-IPP, rc)/cps, row = dot(P-IPP, cc)/rps
 * Falls back to [x, y] if metadata is unavailable.
 */
const worldToPixel = (imageId, worldPt) => {
  const meta = cornerstone.metaData.get("imagePlaneModule", imageId);
  if (
    meta?.imagePositionPatient &&
    meta?.rowCosines &&
    meta?.columnCosines &&
    meta?.rowPixelSpacing != null &&
    meta?.columnPixelSpacing != null
  ) {
    const { imagePositionPatient: ipp, rowCosines: rc, columnCosines: cc, rowPixelSpacing: rps, columnPixelSpacing: cps } = meta;
    const dx = [worldPt[0] - ipp[0], worldPt[1] - ipp[1], worldPt[2] - ipp[2]];
    const col = (dx[0] * rc[0] + dx[1] * rc[1] + dx[2] * rc[2]) / cps;
    const row = (dx[0] * cc[0] + dx[1] * cc[1] + dx[2] * cc[2]) / rps;
    return [col, row];
  }
  // Inverse of the spacing-scaled fallback (see pixelToWorld): col = x/cps, row = y/rps.
  const meta2 = meta ?? {};
  const fps = meta2.rowPixelSpacing ?? 1;
  const fcps = meta2.columnPixelSpacing ?? 1;
  return [worldPt[0] / fcps, worldPt[1] / fps];
};

// ─── Save PR payload collection ───────────────────────────────────────────────

/**
 * Collect all Cornerstone annotations on the given imageId plus the current
 * viewport VOI and camera state, and return a payload ready to POST to the
 * backend save-pr endpoint.
 *
 * @param {object} viewport  - CS3D viewport instance
 * @param {string} imageId   - imageId of the currently displayed frame
 * @param {number} studyId   - backend DB study id (studyData.studyId from context)
 */
/**
 * Annotation UIDs of AI-suggested bounding boxes the user has NOT selected
 * (Detect viewer). They live in the annotation manager (hidden, or re-added
 * when a tag is deselected) but must never be saved into a PR.
 *   annotationDataAI: [imageURL][pathologyId] -> [{ annotation }]  AI suggestions
 *   annotationData:   [imageURL][pathologyId] -> [{ annotation }]  selected / locked / drawn
 * A selected AI box shares its annotationUID with its AI-bucket copy, so it stays.
 */
export const unselectedAIAnnotationUIDs = (annotationData, annotationDataAI) => {
  const uidsOf = (buckets) => {
    const uids = new Set();
    for (const imageURL in buckets ?? {}) {
      for (const pathologyId in buckets[imageURL] ?? {}) {
        const items = buckets[imageURL][pathologyId];
        (Array.isArray(items) ? items : []).forEach((item) => {
          const uid = item?.annotation?.annotationUID;
          if (uid) uids.add(uid);
        });
      }
    }
    return uids;
  };
  const selected = uidsOf(annotationData);
  return new Set([...uidsOf(annotationDataAI)].filter((uid) => !selected.has(uid)));
};

/**
 * Probe value line for a saved PR, from ProbeTool cachedStats (v4). The value
 * is not always one number:
 *   grayscale:         value 123,          modalityUnit "HU" / "" / "raw"
 *   colour (RGB/YBR):  value [r, g, b],    modalityUnit "" / "raw"
 *   calibrated US:     value [x, y],       modalityUnit ["cm", "cm"]
 * Math.round on an array gave "NaN" in the saved text (and .trim() on an array
 * unit threw). Non-numbers are dropped; nothing valid → "" (no value line).
 */
export const formatProbeValueText = (value, modalityUnit) => {
  const cleanUnit = (u) => {
    const t = typeof u === "string" ? u.trim() : "";
    return t && t !== "raw" ? t : "";
  };
  const num = (v) => {
    const n = typeof v === "number" ? v : Number(v);
    return v != null && v !== "" && Number.isFinite(n) ? Math.round(n) : null;
  };
  const withUnit = (n, u) => (u ? `${n} ${u}` : `${n}`);

  if (Array.isArray(value)) {
    if (Array.isArray(modalityUnit)) {
      // One line per component, each with its own unit (calibrated US).
      return value
        .map((v, i) => {
          const n = num(v);
          return n == null ? null : withUnit(n, cleanUnit(modalityUnit[i]));
        })
        .filter(Boolean)
        .join("\n");
    }
    const parts = value.map(num);
    if (!parts.length || parts.some((n) => n == null)) return "";
    return withUnit(parts.join(", "), cleanUnit(modalityUnit));
  }
  const n = num(value);
  return n == null ? "" : withUnit(n, cleanUnit(modalityUnit));
};

export const collectPRSavePayload = (viewport, imageId, studyId, { description = "", creatorName = "", spineLabels = [], excludeAnnotationUIDs = null } = {}) => {
  const imagePlaneMeta = cornerstone.metaData.get("imagePlaneModule", imageId);
  const pixelMeta      = cornerstone.metaData.get("imagePixelModule",  imageId);
  const sopMeta        = cornerstone.metaData.get("sopCommonModule",    imageId);
  const seriesMeta     = cornerstone.metaData.get("generalSeriesModule",imageId);

  const frameOfReferenceUID = imagePlaneMeta?.frameOfReferenceUID ?? "";
  const sopInstanceUID      = (sopMeta?.sopInstanceUID   ?? sopMeta?.SOPInstanceUID   ?? "").trim();
  const seriesInstanceUID   = (seriesMeta?.seriesInstanceUID ?? seriesMeta?.SeriesInstanceUID ?? "").trim();
  // Multi-frame clip: the frame on screen → (0008,1160) Referenced Frame Number,
  // so the PR (and every annotation in it) is scoped to that frame only.
  const referencedFrameNumber = multiFrameNumberOf(imageId);
  // studyInstanceUID is NOT sent — the backend derives it from studyId via the DB.
  const rps = imagePlaneMeta?.rowPixelSpacing    ?? pixelMeta?.rowPixelSpacing    ?? 1;
  const cps = imagePlaneMeta?.columnPixelSpacing ?? pixelMeta?.columnPixelSpacing ?? 1;

  // ── Collect annotations for this frame ──────────────────────────────────
  const mgr = annotation.state.getAnnotationManager();
  // Annotations are keyed in the manager by FrameOfReferenceUID. CR/DX/SC images
  // have NO FrameOfReferenceUID (imagePlaneMeta.frameOfReferenceUID is ""), and the
  // viewport stores their annotations under its own generated key — so a lookup by
  // "" finds nothing and the PR saves with zero annotations. Iterate ALL annotations
  // and filter by referencedImageId (the reliable per-annotation key) instead, so
  // annotations are collected regardless of how the manager keyed the frame.
  const allInManager = typeof mgr.getAllAnnotations === "function"
    ? mgr.getAllAnnotations()
    : Object.values(mgr.getAnnotations(frameOfReferenceUID) ?? {}).flat();
  const excluded = excludeAnnotationUIDs instanceof Set
    ? excludeAnnotationUIDs
    : new Set(excludeAnnotationUIDs ?? []);
  const allAnnotations = allInManager
    .filter(a => a?.metadata?.referencedImageId === imageId)
    // Unselected AI suggestions (Detect viewer) are in the manager but are not
    // the user's annotations — see unselectedAIAnnotationUIDs.
    .filter(a => !excluded.has(a?.annotationUID));

  // flatMap because one Augmento annotation can map to MORE than one DICOM
  // graphic-layer item (an arrow-with-text → a separate ARROW item + TEXT item,
  // mirroring MedDream). A branch returns an object, an array of objects, or null.
  const graphicAnnotations = allAnnotations.flatMap(ann => {
    const tool      = ann.metadata.toolName;
    const T         = cornerstonetools;
    const userLabel = ann.data?.label ?? ann.data?.text ?? "";

    // Compute the measurement VALUE text from cachedStats. This is written to
    // (0070,0006) so the stored value is visible in third-party viewers and is
    // displayed verbatim on reload (Finding 2). prType carries the type to
    // (0070,0068) (Finding 1).
    const stats = Object.values(ann.data?.cachedStats ?? {})[0] ?? {};
    const measurementText = (() => {
      if (tool === T.LengthTool.toolName && stats.length != null)
        return `${Number(stats.length).toFixed(2)} mm`;
      if (tool === T.AngleTool.toolName && stats.angle != null)
        return isNaN(stats.angle) ? String(stats.angle) : `${Number(stats.angle).toFixed(2)}°`;
      if (tool === T.CobbAngleTool.toolName) {
        const a = stats.rho ?? stats.angle;
        return a != null ? `${Number(a).toFixed(2)}°` : "";
      }
      // Strip calibration-type suffix Cornerstone appends to areaUnit
      // (e.g. "mm² A", "mm² CALIBRATED") — keep only the unit symbol.
      const cleanUnit = (u) => (u || "mm²").split(" ")[0];
      if (tool === T.EllipticalROITool.toolName && stats.area != null)
        return `${Number(stats.area).toFixed(2)} ${cleanUnit(stats.areaUnit)}`;
      if (tool === T.CircleROITool.toolName && stats.area != null)
        return `${Number(stats.area).toFixed(2)} ${cleanUnit(stats.areaUnit)}`;
      // Rectangle ROI carries an area too (bug 3): without a stored value the
      // closed-polyline restore shows no measurement in either viewer.
      if (tool === T.RectangleROITool.toolName && stats.area != null)
        return `${Number(stats.area).toFixed(2)} ${cleanUnit(stats.areaUnit)}`;
      // Closed freehand ROI (FlexPoly shape drawn in Basic): area (bug 24).
      if (tool === T.PlanarFreehandROITool.toolName && stats.area != null)
        return `Area: ${Number(stats.area).toFixed(2)} ${cleanUnit(stats.areaUnit)}`;
      // Bidirectional (cross measurement): length × width (bug 10).
      if (tool === T.BidirectionalTool.toolName) {
        const L = stats.length, W = stats.width, u = stats.unit || "mm";
        if (L != null && W != null)
          return `L: ${Number(L).toFixed(2)} ${u} W: ${Number(W).toFixed(2)} ${u}`;
        if (L != null) return `L: ${Number(L).toFixed(2)} ${u}`;
      }
      return "";
    })();

    // Bidirectional (FlexPoly) — two intersecting lines: [p0,p1]=long axis (L),
    // [p2,p3]=short axis (W). Stored as two separate 2-point POLYLINE items (one
    // DICOM graphic-layer item each), same pattern as MedDream multi-segment tools.
    // The L×W measurement text is stored on the first item (bug 10).
    if (tool === T.BidirectionalTool.toolName) {
      const pts = ann.data.handles?.points ?? [];
      if (pts.length < 4) return null;
      const [p0, p1, p2, p3] = pts;
      const longAxis  = [p0, p1].flatMap(pt => worldToPixel(imageId, pt));
      const shortAxis = [p2, p3].flatMap(pt => worldToPixel(imageId, pt));
      return [
        { type: "POLYLINE", coords: longAxis,  prType: "Measurement bidirectional", value: measurementText, label: userLabel },
        { type: "POLYLINE", coords: shortAxis, prType: "Measurement bidirectional", value: "", label: "" },
      ];
    }

    if (tool === T.PlanarFreehandROITool.toolName) {
      const poly   = ann.data.contour?.polyline ?? [];
      const closed = ann.data.contour?.closed   ?? false;
      const coords = poly.flatMap(pt => worldToPixel(imageId, pt));
      if (closed && poly.length > 0) {
        // Repeat first point to close the DICOM POLYLINE
        coords.push(...worldToPixel(imageId, poly[0]));
      }
      if (closed && measurementText) {
        // Closed ROI with a computed area → encode as "Measurement flexpoly" so
        // MedDream recognises it as a polygon ROI and shows the area (bug 24).
        return { type: "POLYLINE", coords, prType: "Measurement flexpoly", value: measurementText, label: userLabel };
      }
      // Open freehand (pencil) — geometry only. Send both value and label empty so
      // the backend writes no text object (stray dimension in MedDream, bug 25).
      return { type: "POLYLINE", coords, prType: "Measurement pencil", value: "", label: "" };
    }

    // Length measurement (2-point polyline)
    if (tool === T.LengthTool.toolName) {
      const coords = (ann.data.handles?.points ?? []).flatMap(pt => worldToPixel(imageId, pt));
      return { type: "POLYLINE", coords, prType: "Measurement polyline", value: measurementText, label: userLabel };
    }

    // Angle (3-point polyline: start, vertex, end)
    if (tool === T.AngleTool.toolName) {
      const coords = (ann.data.handles?.points ?? []).flatMap(pt => worldToPixel(imageId, pt));
      return { type: "POLYLINE", coords, prType: "Measurement angle", value: measurementText, label: userLabel };
    }

    // Cobb angle (two 2-point arms). value = angle; value2 = distance between the
    // two lines' midpoints (centers), computed in world space (= mm). MedDream
    // stores a Cobb as angle + this length (two text values), so we mirror that —
    // and the backend emits the arms as two separate segments (bug 14).
    if (tool === T.CobbAngleTool.toolName) {
      const wpts  = ann.data.handles?.points ?? [];
      const coords = wpts.flatMap(pt => worldToPixel(imageId, pt));
      let lengthText = "";
      if (wpts.length >= 4) {
        const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
        const m1 = mid(wpts[0], wpts[1]);
        const m2 = mid(wpts[2], wpts[3]);
        const d  = Math.hypot(m2[0] - m1[0], m2[1] - m1[1], m2[2] - m1[2]);
        lengthText = `${d.toFixed(2)} mm`;
      }
      return { type: "POLYLINE", coords, prType: "Measurement cobb-angle", value: measurementText, value2: lengthText, label: userLabel };
    }

    // ArrowAnnotateTool carries [tail, head] plus text. MedDream stores an arrow
    // and its text as SEPARATE graphic-layer items, so:
    //   • collapsed endpoints (no real arrow) → a single standalone TEXT (bug 6);
    //   • a real arrow → an ARROW item (compound + polyline, NO text) AND, when
    //     there is text, a separate TEXT item (bug 26 + arrow/text separation).
    if (tool === T.ArrowAnnotateTool.toolName) {
      const pts  = ann.data.handles?.points ?? [];
      const text = ann.data.text ?? userLabel ?? "";
      // arrowFirst:true (our tool config, matching Advance viewer) → points[0]=head/tip,
      // points[1]=tail.
      const arrowFirst = ann.data.handles?.arrowFirst !== false; // default true
      const tip  = arrowFirst ? (pts[0] ?? [0,0,0]) : (pts[1] ?? [0,0,0]);
      const tail = arrowFirst ? (pts[1] ?? tip)     : (pts[0] ?? [0,0,0]);
      const cTail = worldToPixel(imageId, tail);
      const cHead = worldToPixel(imageId, tip);   // DICOM "head" = tip
      const distPx = Math.hypot(cHead[0] - cTail[0], cHead[1] - cTail[1]);

      if (distPx < 1) {
        return { type: "TEXT", coords: cTail, prType: "Measurement text", value: text, label: text };
      }

      // Store the arrow as [head, tail] (arrowhead point FIRST). The Advance viewer
      // (MedDream) draws the arrowhead at the FIRST point of (0070,0022) — verified
      // from a MedDream-authored arrow (head = first stored point, text at the last).
      // Storing [tail, head] put the arrowhead on the opposite (tail) end in MedDream.
      const arrowItem = {
        type: "POLYLINE", coords: [...cHead, ...cTail],
        prType: "Measurement arrow-point", value: "", label: "", arrow: true,
      };
      if (!text) return arrowItem;

      // Anchor the text at the textbox position if the user moved it, else a little
      // away from the arrow tail (not exactly on it, so the label reads as separate).
      const tb = ann.data.handles?.textBox?.worldPosition;
      // Anchor the text at the textbox position if moved, else near the tail
      // (shaft start) — away from the arrowhead tip so they don't overlap.
      const cText = (tb && (tb[0] || tb[1] || tb[2]))
        ? worldToPixel(imageId, tb)
        : [cTail[0] + 15, cTail[1] + 15];
      const textItem = { type: "TEXT", coords: cText, prType: "Measurement text", value: text, label: text };
      return [arrowItem, textItem];
    }

    // Probe point — store both the pixel value (HU) AND the pixel coordinates so
    // MedDream shows the full probe label. Previously only the HU was stored, so
    // coordinates were missing in the Advance viewer.
    if (tool === T.ProbeTool.toolName) {
      const pt         = ann.data.handles?.points?.[0] ?? [0, 0, 0];
      const [col, row] = worldToPixel(imageId, pt);
      // ProbeTool cachedStats.index = [i, j, k] — the 3-D voxel index,
      // matching the native tool's own display "(i, j, k)" on line 1.
      // Line 2: HU value, same layout as the tool's defaultGetTextLines.
      const idx = stats.index;
      const coordStr = idx
        ? `(${Math.round(idx[0])}, ${Math.round(idx[1])}, ${Math.round(idx[2])})`
        : `(${Math.round(col)}, ${Math.round(row)})`;
      const valueText = formatProbeValueText(stats.value, stats.modalityUnit);
      const probeValue = valueText ? `${coordStr}\n${valueText}` : coordStr;
      return { type: "POINT", coords: [col, row], prType: "Measurement probe", value: probeValue, label: userLabel };
    }

    // Circle ROI (center + edge = 2 handle points)
    if (tool === T.CircleROITool.toolName) {
      const coords = (ann.data.handles?.points ?? []).flatMap(pt => worldToPixel(imageId, pt));
      return { type: "CIRCLE", coords, prType: "Measurement circle", value: measurementText, label: userLabel };
    }

    // Ellipse ROI (4 handle points: bottom, top, left, right)
    if (tool === T.EllipticalROITool.toolName) {
      const pts = ann.data.handles?.points ?? [];
      if (pts.length < 4) return null;
      // DICOM PS3.3 C.10.5.1.2: the first two points MUST be the major-axis endpoints
      // and the next two the minor-axis endpoints. Cornerstone stores handles as
      // [axis0_p0, axis0_p1, axis1_p0, axis1_p1] where axis0 is the vertical pair and
      // axis1 is the horizontal pair. For a horizontal ellipse the horizontal pair is
      // the major axis — putting the shorter vertical pair first would make third-party
      // viewers (e.g. MedDream) render it as a vertical ellipse.
      const d3 = (a, b) => Math.hypot(b[0]-a[0], b[1]-a[1], b[2]-a[2]);
      const [p0, p1, p2, p3] = pts;
      const orderedPts = d3(p0, p1) >= d3(p2, p3)
        ? [p0, p1, p2, p3]   // axis0 is already the major axis
        : [p2, p3, p0, p1];  // axis1 is the major axis — swap so it comes first
      const coords = orderedPts.flatMap(pt => worldToPixel(imageId, pt));
      return { type: "ELLIPSE", coords, prType: "Measurement ellipse", value: measurementText, label: userLabel };
    }

    // Rectangle ROI — stored as 4 corners: [bottomLeft, bottomRight, topLeft, topRight]
    // DICOM has no RECTANGLE type; save as a closed 5-point POLYLINE so the restore
    // path renders it as a closed polygon.
    if (tool === T.RectangleROITool.toolName) {
      const pts = ann.data.handles?.points ?? [];
      if (pts.length < 4) return null;
      // Clockwise order: topLeft → topRight → bottomRight → bottomLeft → topLeft (close)
      const ordered = [pts[2], pts[3], pts[1], pts[0], pts[2]];
      const coords = ordered.flatMap(pt => worldToPixel(imageId, pt));
      // Always store a measurement (bug 3). cachedStats.area can be null if the
      // ROI is saved before Cornerstone's throttled stat computation runs, so fall
      // back to the geometric area = width × height of the two adjacent sides in
      // world space (= mm²). Without a value MedDream shows no measurement.
      let value = measurementText;
      if (!value) {
        const d3 = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
        const w = d3(pts[2], pts[3]); // top side
        const h = d3(pts[3], pts[1]); // right side
        if (w > 0 && h > 0) value = `${(w * h).toFixed(2)} mm²`;
      }
      return { type: "POLYLINE", coords, prType: "Measurement rectangle-roi", value, label: userLabel };
    }

    return null;
  }).filter(Boolean);

  // ── Spine-labelling annotations ─────────────────────────────────────────
  // Spine labels are NOT Cornerstone annotations — they live in React state
  // (ViewportContext.spineLabels) and are passed in here. Each label is a
  // vertebra name (e.g. "C1") anchored at a world position on a specific image.
  // Encode each as a POINT graphic object + a "C1" text label under the
  // "Measurement spine" layer — byte-compatible with the Advance viewer (MedDream),
  // which stores each label as a POINT in (0070,0009) plus a Text Object in
  // (0070,0008). Labels may span MULTIPLE images, so each carries its own
  // referenced SOP/Series and its coords are computed against ITS image — the
  // backend groups them per image into separate Graphic Annotation items.
  if (Array.isArray(spineLabels) && spineLabels.length) {
    for (const lbl of spineLabels) {
      if (!lbl?.vertebra || !lbl?.worldPos) continue;
      // Prefer the image the label was placed on; fall back to the saved image.
      const labelImageId = lbl.imageId || imageId;
      const labelPlane = cornerstone.metaData.get("imagePlaneModule", labelImageId);
      // If the label's own image has no loaded geometry, fall back to the saved
      // image so the conversion still produces sensible pixel coordinates.
      const convImageId = labelPlane ? labelImageId : imageId;
      const [col, row] = worldToPixel(convImageId, lbl.worldPos);
      if (col == null || row == null || Number.isNaN(col) || Number.isNaN(row)) continue;

      const labelSopMeta    = cornerstone.metaData.get("sopCommonModule",    convImageId);
      const labelSeriesMeta = cornerstone.metaData.get("generalSeriesModule", convImageId);
      const labelSOP =
        lbl.sopInstanceUID ||
        (labelSopMeta?.sopInstanceUID ?? labelSopMeta?.SOPInstanceUID)?.trim() ||
        sopInstanceUID;
      const labelSeries =
        (labelSeriesMeta?.seriesInstanceUID ?? labelSeriesMeta?.SeriesInstanceUID)?.trim() ||
        seriesInstanceUID;

      graphicAnnotations.push({
        type: "POINT",
        coords: [col, row],
        prType: "Measurement spine",
        value: lbl.vertebra,   // (0070,0006) Unformatted Text Value = the label
        label: lbl.vertebra,
        referencedSOPInstanceUID:    labelSOP,
        referencedSeriesInstanceUID: labelSeries,
        referencedFrameNumber:       multiFrameNumberOf(convImageId) ?? undefined,
      });
    }
  }

  // ── VOI LUT from current viewport ───────────────────────────────────────
  const voiRange = viewport.getProperties?.()?.voiRange;
  const voiLUT   = voiRange != null
    ? { windowCenter: (voiRange.upper + voiRange.lower) / 2,
        windowWidth:   voiRange.upper  - voiRange.lower }
    : null;

  // ── Displayed area from camera (zoom/pan) ────────────────────────────────
  // Store ONLY the visible image sub-rectangle in IMAGE-PIXEL coordinates
  // (1-based), clamped to the image bounds — exactly what MedDream (the Advance
  // Viewer) writes: always SCALE TO FIT, never a MAGNIFY magnification ratio.
  //
  // Zoom is deliberately NOT stored as an absolute number. It is encoded
  // implicitly as the SIZE of this rectangle. On load the rectangle is scaled to
  // fit whatever tile it lands in, so the effective magnification is derived from
  // the current layout: a 1x1 tile shows the same rectangle ~2x bigger than a 2x2
  // tile, a 3x3 tile smaller, etc. This makes PR zoom layout-relative and matches
  // the Advance Viewer, instead of pinning a fixed per-pixel zoom via MAGNIFY.
  //
  // When zoomed out (the visible rect spills past the image edge) clamping yields
  // the full-image corners — so a zoomed-out save reloads as fit-to-tile, which is
  // also how MedDream behaves (it cannot represent the surrounding margins either).
  // The backend always adds (0070,0102) Pixel Aspect Ratio = 1\1.
  const camera = viewport.getCamera?.();
  let displayedArea = null;
  if (camera?.parallelScale != null && camera?.focalPoint) {
    const canvas     = viewport.canvas;
    const vpW        = canvas.clientWidth  || canvas.width  || 512;
    const vpH        = canvas.clientHeight || canvas.height || 512;
    const halfWorldH = camera.parallelScale;
    const halfWorldW = halfWorldH * (vpW / vpH);
    const centerPx   = worldToPixel(imageId, camera.focalPoint);

    const cols = pixelMeta?.columns ?? imagePlaneMeta?.columns ?? null;
    const rows = pixelMeta?.rows    ?? imagePlaneMeta?.rows    ?? null;

    // Visible image rectangle in pixel coords (may extend past the image when
    // zoomed out — the clamp below reduces it to the full image in that case).
    const tlCol = centerPx[0] - halfWorldW / cps;
    const tlRow = centerPx[1] - halfWorldH / rps;
    const brCol = centerPx[0] + halfWorldW / cps;
    const brRow = centerPx[1] + halfWorldH / rps;

    // DICOM pixel corners are 1-based. Apply +1 consistently to BOTH TL and BR so
    // the stored center = (tl+br)/2 = centerPx+1 and the apply path converts back
    // to 0-based by subtracting 1 before pixelToWorld.
    const clampCol = (v) => cols != null ? Math.min(Math.max(v, 1), cols) : Math.max(v, 1);
    const clampRow = (v) => rows != null ? Math.min(Math.max(v, 1), rows) : Math.max(v, 1);
    displayedArea = {
      topLeft:     [Math.round(clampCol(tlCol + 1)), Math.round(clampRow(tlRow + 1))],
      bottomRight: [Math.round(clampCol(brCol + 1)), Math.round(clampRow(brRow + 1))],
      presentationSizeMode: "SCALE TO FIT",
    };
  }

  return {
    studyId,
    seriesInstanceUID,
    sopInstanceUID,
    referencedFrameNumber: referencedFrameNumber ?? undefined,
    // Client-generated UID for the NEW PR series about to be created. The backend
    // uses this verbatim (falling back to its own UID only if omitted), so the
    // caller can look up this exact series once async DICOM ingestion completes,
    // instead of re-fetching the whole study (see waitForSeriesReady / getSeriesByUID).
    newSeriesInstanceUID: generateDicomUID(),
    graphicAnnotations,
    voiLUT,
    displayedArea,
    description: description.trim() || undefined,
    creatorName: creatorName.trim() || undefined,
  };
};

/**
 * POST the PR save payload to the backend.
 * Returns the Axios response (data.data.storageKey on success).
 */
export const savePRAnnotations = (payload) =>
  axiosInterceptor.post("/jwtServices/study/save-pr", payload);

// ─── Annotation factory ───────────────────────────────────────────────────────

// Compute angle (degrees) between two 3-D vectors sharing a vertex (p2).
const _computeAngleDeg = (p1, p2, p3) => {
  const v1 = [p1[0]-p2[0], p1[1]-p2[1], p1[2]-p2[2]];
  const v2 = [p3[0]-p2[0], p3[1]-p2[1], p3[2]-p2[2]];
  const dot = v1[0]*v2[0] + v1[1]*v2[1] + v1[2]*v2[2];
  const len1 = Math.hypot(...v1);
  const len2 = Math.hypot(...v2);
  if (len1 === 0 || len2 === 0) return null;
  return Math.acos(Math.min(Math.max(dot / (len1 * len2), -1), 1)) * (180 / Math.PI);
};

// Compute ellipse area (mm²) from four handle world-points (any axis ordering).
// π × semiAxis1 × semiAxis2 — order-independent since multiplication is commutative.
const _computeEllipseAreaMm2 = (pts) => {
  if (pts.length < 4) return null;
  const dist = (a, b) => Math.hypot(b[0]-a[0], b[1]-a[1], b[2]-a[2]);
  const semi0 = dist(pts[0], pts[1]) / 2;
  const semi1 = dist(pts[2], pts[3]) / 2;
  return Math.PI * semi0 * semi1;
};

// Pre-populate cachedStats for tools that can compute measurements purely from
// world-space handle positions. This ensures measurement text is visible immediately
// when the annotation is added from a PR file, without waiting for a second render
// cycle (which may not occur when the viewport is on a different frame).
const _buildPrecomputedStats = (toolName, worldPoints, imageId) => {
  const key = `imageId:${imageId}`;
  if (toolName === cornerstonetools.AngleTool.toolName && worldPoints.length === 3) {
    const angle = _computeAngleDeg(worldPoints[0], worldPoints[1], worldPoints[2]);
    if (angle != null) return { [key]: { angle } };
  }
  if (toolName === cornerstonetools.EllipticalROITool.toolName && worldPoints.length >= 4) {
    const area = _computeEllipseAreaMm2(worldPoints);
    if (area != null) return { [key]: { area, areaUnit: "mm²", Modality: null, mean: null, max: null, stdDev: null } };
  }
  return {};
};

// Build a measurement tool's cachedStats inner object from the STORED DICOM
// value text (0070,0006) so the viewer displays the stored number rather than
// recomputing from geometry (Finding 2/15). Returns null when the tool has no
// single-scalar stat or the value carries no number.
const _statsFromStoredValue = (toolName, value) => {
  const num = parseLeadingNumber(value);
  if (num == null) return null;
  const T = cornerstonetools;
  if (toolName === T.LengthTool.toolName)
    return { length: num, unit: parseTrailingUnit(value) || "mm" };
  if (toolName === T.AngleTool.toolName)
    return { angle: num };
  if (toolName === T.CobbAngleTool.toolName)
    return { angle: num };
  if (toolName === T.EllipticalROITool.toolName || toolName === T.CircleROITool.toolName)
    return { area: num, areaUnit: parseTrailingUnit(value) || "mm²", mean: null, max: null, stdDev: null, Modality: null };
  return null;
};

const makeAnnotation = (toolName, worldPoints, imageId, frameOfReferenceUID, label = "", statValues = null) => {
  let cachedStats = {};
  if (statValues) {
    // Display the stored DICOM value verbatim — do not recompute (Finding 2).
    cachedStats = { [`imageId:${imageId}`]: statValues };
  } else {
    try {
      cachedStats = _buildPrecomputedStats(toolName, worldPoints, imageId);
    } catch (_) {}
  }

  // If cachedStats is pre-populated (we computed a value), mark the annotation as NOT
  // invalidated so Cornerstone uses our value directly without scheduling a throttled
  // _calculateCachedStats call 100 ms later.  That throttled call fires
  // triggerAnnotationModified outside the React render cycle and can crash the viewer
  // with a ".includes on undefined" error when the viewport state has partially changed.
  // When cachedStats is empty, keep invalidated:true so Cornerstone's own synchronous
  // first-render path computes the stats normally (same behaviour as before PR loading).
  const precomputed = Object.keys(cachedStats).length > 0;

  return {
    annotationUID: crypto.randomUUID(),
    metadata: {
      toolName,
      referencedImageId: imageId,
      // Pass the FoR through verbatim (do NOT coerce undefined→""): CR/DX/SC viewports
      // report undefined and the manager/render path key annotations under that value.
      // Coercing to "" stores them under a different key and they never render.
      FrameOfReferenceUID: frameOfReferenceUID,
      viewPlaneNormal: [0, 0, 1],
      viewUp: [0, -1, 0],
    },
    data: {
      handles: {
        points: worldPoints,
        activeHandleIndex: null,
        textBox: {
          hasMoved: false,
          worldPosition: [0, 0, 0],
          worldBoundingBox: {
            topLeft: [0, 0, 0],
            topRight: [0, 0, 0],
            bottomLeft: [0, 0, 0],
            bottomRight: [0, 0, 0],
          },
        },
      },
      cachedStats,
      label: label,
    },
    isLocked: false,
    isVisible: true,
    highlighted: false,
    invalidated: !precomputed,
  };
};

// ─── Standard DICOM annotation rendering ─────────────────────────────────────

// Text colour MedDream uses for PR text objects that carry no (0070,0231) style.
const PR_PLAIN_TEXT_COLOR = "rgb(255, 165, 0)";
// Box MedDream draws behind a "+n" measurement marker.
const PR_MARKER_BACKGROUND = "rgb(0, 0, 0)";
// PRTextTool.toolName (helpers/PRTextTool.jsx) — kept as a literal so this
// service doesn't pull Cornerstone's drawing module in.
const PR_TEXT_TOOL_NAME = "PRText";

/**
 * Per-annotation Cornerstone style for an annotation-set object, pinned for
 * every interaction state (normal / highlighted / selected / locked) so hover
 * or selection never swaps in Cornerstone's yellow/green.
 *  • shapes: their stored colour (when present) at 1 px — (0070,0253) is 1.0;
 *  • texts:  LabelTool takes its text colour from textBoxColor* (NOT color*),
 *            default MedDream orange; "+n" markers get the dark box.
 */
export const plainObjectStyle = ({ isText, color, boxed }) => {
  const states = (prop, value) => ({
    [prop]: value,
    [`${prop}Highlighted`]: value,
    [`${prop}Selected`]: value,
    [`${prop}Locked`]: value,
  });
  if (isText) {
    return {
      ...states("textBoxColor", color || PR_PLAIN_TEXT_COLOR),
      ...(boxed ? { textBoxBackground: PR_MARKER_BACKGROUND } : {}),
    };
  }
  return { ...(color ? states("color", color) : {}), lineWidth: "1" };
};

/**
 * @param {string}   defaultImageId  - image an object lands on when resolveTargets
 *                                     is absent (or returns nothing for it)
 * @param {Function} [resolveTargets] - entry → imageId[]: the frame(s) a graphic
 *                                     object is scoped to (multi-frame PRs place
 *                                     each object on its own referenced frame)
 */
const applyGraphicAnnotations = (prData, viewport, defaultImageId, frameOfReferenceUID, prUrl = null, resolveTargets = null) => {
  if (!prData.graphicAnnotations?.length) return;
  const annotationManager = annotation.state.getAnnotationManager();

  // ── Anisotropic-pixel correction for PR graphic coordinates ──────────────────
  // The Advance viewer (MedDream) authors graphic coords (0070,0022) assuming
  // SQUARE pixels, and renders the image square too. Some images have non-square
  // pixels — e.g. a CT scout / "Non Xray" localizer with PixelSpacing 0.5455\0.7015
  // (column spacing ≠ row spacing). Our pixelToWorld() honors the true anisotropic
  // spacing, so a column coordinate maps to a wider world span than MedDream
  // intended → the annotation is stretched horizontally (by colSpacing/rowSpacing)
  // AND shifted toward a corner (the reported scout-ellipse bug). The Basic viewport
  // also displays this scout with square pixels, so the annotation must use the same
  // square-pixel convention to stay aligned with the image.
  //
  // Fix: pre-scale the COLUMN coordinate by rowSpacing/colSpacing before pixelToWorld
  // so the column axis is mapped at the row spacing (uniform/square), matching both
  // MedDream's authoring and the displayed image. No-op for isotropic images
  // (factor == 1), so the common 512×512 path is untouched.
  const _plane = cornerstone.metaData.get("imagePlaneModule", defaultImageId);
  const _rps = _plane?.rowPixelSpacing;
  const _cps = _plane?.columnPixelSpacing;
  let colAspectFix = 1;
  if (_rps > 0 && _cps > 0 && Math.abs(_cps / _rps - 1) > 0.005) {
    colAspectFix = _rps / _cps;
  }
  // FrameOfReferenceUID from DICOM metadata may be empty when metadata hasn't
  // loaded yet. Fall back to the viewport's own FrameOfReferenceUID so all
  // annotation tools find their annotations via getAnnotations(element) →
  // viewport.getFrameOfReferenceUID(). CRITICAL for CR/DX/SC: those viewports
  // report getFrameOfReferenceUID() === undefined, and the annotation manager keys
  // annotations under that value (coerced to "undefined"). The render path then
  // looks them up under the SAME undefined key. Forcing "" here (a DIFFERENT key)
  // orphaned PR annotations so they were never drawn — match the viewport's value
  // EXACTLY, including undefined, instead of coercing to "".
  // eslint-disable-next-line no-param-reassign
  frameOfReferenceUID = frameOfReferenceUID || viewport.getFrameOfReferenceUID?.();

  // Stable-identity prefix: PR SOP Instance UID (falls back to the source URL).
  // Combined with the object index this yields a deterministic annotationUID, so
  // a second apply of the same PR is idempotent (bugs 4, 16).
  const prKey = prData.sopInstanceUID || prUrl || "pr";

  // Places ONE graphic object on ONE image. uidSuffix distinguishes the copies
  // of an object that spans several frames (stable: keyed by the frame imageId).
  const placeGraphicAnnotation = ({ type, numPoints, coords, label, prType, compoundType, groupLead, plain, color, boxed, copyText, boxBottomLeft }, idx, imageId, uidSuffix) => {
    const points = [];
    for (let i = 0; i + 1 < coords.length; i += 2) {
      // Scale the column coord so anisotropic-pixel images map at the row (square)
      // spacing, matching MedDream's authoring and the square-pixel display (no-op
      // when colAspectFix == 1).
      points.push(pixelToWorld(imageId, coords[i] * colAspectFix, coords[i + 1]));
    }
    if (!points.length) return;

    // Resolve the tool from the canonical type carriers, never from point count
    // when a marker is present (Findings 1/3/9). Legacy/Weasis/Augmento-origin
    // objects with no marker fall back to geometry inside resolvePRKind.
    const kind = resolvePRKind({ prType, compoundType, graphicType: type, numPoints });
    const T = cornerstonetools;

    // Value-display rules: pencil carries no measurement (Finding/bug 25); a
    // Cobb made of multiple polylines shows its angle once (on the lead object),
    // never per-segment lengths (bugs 9/14). All other kinds show the stored value.
    const displayLabel =
      kind === PR_KIND.PENCIL ? "" :
      (kind === PR_KIND.COBB && !groupLead) ? "" :
      label;

    // A plain text label rendered AT a point — uses LabelTool so NO arrow and
    // NO leader line is drawn, only the textbox. LabelTool uses referencedImageId
    // for frame lookup (same as all annotation tools), so it renders reliably
    // regardless of FrameOfReferenceUID.
    const makeValueTextbox = (worldPt, text) => ({
      annotationUID: crypto.randomUUID(),
      metadata: {
        toolName: cornerstonetools.LabelTool.toolName,
        referencedImageId: imageId,
        FrameOfReferenceUID: frameOfReferenceUID,
        viewPlaneNormal: [0, 0, 1],
        viewUp: [0, -1, 0],
      },
      data: {
        // Cornerstone 4.x LabelTool draws ONLY data.label (an empty label is
        // skipped); `text` is kept for readers of the older field.
        label: text,
        text,
        handles: {
          points: [worldPt],
          activeHandleIndex: null,
          textBox: {
            hasMoved: true,
            worldPosition: worldPt,
            worldBoundingBox: { topLeft: worldPt, topRight: worldPt, bottomLeft: worldPt, bottomRight: worldPt },
          },
        },
        cachedStats: {},
      },
      isLocked: false, isVisible: true, highlighted: false, invalidated: false,
    });

    let ann = null;
    let extraAnn = null;
    if (plain) {
      // Annotation-set object (see extractPRData): drawn exactly as stored,
      // never as a measuring tool — the values are in the PR's own texts.
      if (type.toUpperCase() === "TEXT") {
        ann = makeValueTextbox(points[0], label);
        if (copyText) {
          // PRTextTool = LabelTool + a copy button that copies data.copyText.
          ann.metadata.toolName = PR_TEXT_TOOL_NAME;
          ann.data.copyText = copyText;
        }
        if (boxBottomLeft) {
          // World position of the text box's bottom-left: PRTextTool sizes the
          // font from the box's on-screen height so text scales with zoom.
          ann.metadata.toolName = PR_TEXT_TOOL_NAME;
          ann.data.boxBottomLeftWorld = pixelToWorld(imageId, boxBottomLeft[0] * colAspectFix, boxBottomLeft[1]);
        }
      } else {
        let polyPoints = points;
        let closed =
          coords.length >= 6 &&
          Math.abs(coords[0] - coords[coords.length - 2]) < 0.5 &&
          Math.abs(coords[1] - coords[coords.length - 1]) < 0.5;
        if (closed) polyPoints = points.slice(0, -1);
        if (type.toUpperCase() === "POINT" || polyPoints.length < 2) {
          // A POINT has no extent — mark it with a small ring (radius in pixels).
          const [cx, cy] = coords;
          const r = 4;
          polyPoints = Array.from({ length: 12 }, (_, k) => {
            const t = (k / 12) * 2 * Math.PI;
            return pixelToWorld(imageId, (cx + r * Math.cos(t)) * colAspectFix, cy + r * Math.sin(t));
          });
          closed = true;
        }
        // statValues {} → invalidated:false → no recomputed length / area, and
        // suppressStats keeps freehandGetTextLines silent even if it recomputes.
        ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "", {});
        ann.data.contour = { polyline: polyPoints, closed };
        ann.data.suppressStats = true;
      }
    } else switch (kind) {
      case PR_KIND.PROBE:
        ann = makeAnnotation(T.ProbeTool.toolName, [points[0]], imageId, frameOfReferenceUID, displayLabel);
        break;

      case PR_KIND.BIDIRECTIONAL:
        // Bidirectional (FlexPoly): 4 handle points [p0,p1]=long axis, [p2,p3]=short.
        // Reconstruct as native BidirectionalTool and let it recompute L×W so the
        // measurements display correctly (the stored value string is in `label` but
        // the native tool renders its own formatted L / W lines from cachedStats).
        if (points.length >= 4)
          ann = makeAnnotation(T.BidirectionalTool.toolName,
            [points[0], points[1], points[2], points[3]], imageId, frameOfReferenceUID, displayLabel);
        break;

      case PR_KIND.ARROW: {
        // We store the arrow as [head, tail] — arrowhead at the FIRST point, matching
        // the Advance viewer (MedDream) convention. Our tool config is arrowFirst:true
        // → arrowhead at handles.points[0]. So read head = first stored point, tail =
        // last, and place them as points:[head, tail] (head keeps the arrowhead).
        const head = points[0];
        const tail = points[points.length - 1] ?? head;
        ann = {
          annotationUID: crypto.randomUUID(),
          metadata: {
            toolName: T.ArrowAnnotateTool.toolName,
            referencedImageId: imageId,
            FrameOfReferenceUID: frameOfReferenceUID, // verbatim — see makeAnnotation note
            viewPlaneNormal: [0, 0, 1],
            viewUp: [0, -1, 0],
          },
          data: {
            // Cornerstone 4.x ArrowAnnotateTool renders data.label; keep text too.
            label: displayLabel,
            text: displayLabel,
            handles: {
              points: [head, tail],
              arrowFirst: true,
              activeHandleIndex: null,
              textBox: { hasMoved: false, worldPosition: [0, 0, 0],
                worldBoundingBox: { topLeft: [0,0,0], topRight: [0,0,0], bottomLeft: [0,0,0], bottomRight: [0,0,0] } },
            },
            cachedStats: {},
          },
          isLocked: false, isVisible: true, highlighted: false, invalidated: true,
        };
        break;
      }

      case PR_KIND.FLEXPOLY: {
        // FlexPoly from MedDream: a closed polygon stored as a single multi-point
        // POLYLINE with layer desc "Measurement flexpoly". The stored value is the
        // area (e.g. "Area: 96.58 cm²"). Render as a closed PlanarFreehandROI and
        // show the stored area via storedLabel (same verbatim mechanism as bug 15 —
        // avoids roundNumber truncation and the FrameOfReferenceUID key-mismatch
        // that broke the LabelTool approach).
        const isClosed =
          coords.length >= 4 &&
          Math.abs(coords[0] - coords[coords.length - 2]) < 0.5 &&
          Math.abs(coords[1] - coords[coords.length - 1]) < 0.5;
        const polyPts = isClosed ? points.slice(0, -1) : points;
        // Let Cornerstone recompute stats (no statValues / no storedLabel) so
        // the full live stats render: area + mean + max + std dev. The annotation
        // is NOT invalidated initially (makeAnnotation default) — Cornerstone will
        // compute on first render via _calculateStatsIfActive when it detects the
        // missing .unit. This gives parity with a user-drawn closed freehand.
        ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "");
        ann.data.contour = { polyline: polyPts, closed: true };
        // Force recompute on first render by marking invalidated.
        ann.invalidated = true;
        break;
      }

      case PR_KIND.TEXT:
        // Text-only annotation → Label tool: plain text drawn AT its position with
        // NO arrow and NO leader line (a text added in MedDream shows as just text
        // at the exact spot in Basic).
        ann = makeValueTextbox(points[0], displayLabel);
        break;

      case PR_KIND.LENGTH:
        if (points.length >= 2)
          ann = makeAnnotation(T.LengthTool.toolName, [points[0], points[1]], imageId, frameOfReferenceUID, displayLabel,
            _statsFromStoredValue(T.LengthTool.toolName, label));
        break;

      case PR_KIND.ANGLE:
        if (points.length >= 3)
          ann = makeAnnotation(T.AngleTool.toolName, [points[0], points[1], points[2]], imageId, frameOfReferenceUID, displayLabel,
            _statsFromStoredValue(T.AngleTool.toolName, label));
        break;

      case PR_KIND.COBB:
        // Cobb angle → NATIVE CobbAngleTool from the 4 arm points (both viewers'
        // forms are normalised to 4 points in extractPRData). NO statValues: the
        // tool MUST recompute on render — its render path reads cachedStats.points
        // (set only by the recompute) to draw the dotted link line, then shows the
        // angle in its own linked textbox. This makes a loaded Cobb render exactly
        // like one drawn in Basic — solid arms + dotted link line + angle, with no
        // per-segment lengths and no arrow-text label (bugs 9/14).
        if (points.length >= 4) {
          ann = makeAnnotation(T.CobbAngleTool.toolName,
            [points[0], points[1], points[2], points[3]], imageId, frameOfReferenceUID, "");
        } else {
          // Degenerate fallback: render the segment(s) as a plain (non-measuring) path.
          ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "", {});
          ann.data.contour = { polyline: points, closed: false };
        }
        break;

      case PR_KIND.RECTANGLE: {
        // Rectangle ROI travels as a closed polygon — render as a closed freehand
        // polygon (Augmento has no native RECTANGLE restore path). Suppress the
        // freehand's own recomputed area/perimeter and show the STORED ROI value
        // (e.g. "Mean: … SD: …") as a textbox, honouring Finding 2.
        const isClosed =
          coords.length >= 4 &&
          Math.abs(coords[0] - coords[coords.length - 2]) < 0.5 &&
          Math.abs(coords[1] - coords[coords.length - 1]) < 0.5;
        const polyPoints = isClosed ? points.slice(0, -1) : points;
        ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "", {});
        ann.data.contour = { polyline: polyPoints, closed: true };
        if (label) {
          const cx = polyPoints.reduce((s, p) => s + p[0], 0) / polyPoints.length;
          const cy = polyPoints.reduce((s, p) => s + p[1], 0) / polyPoints.length;
          const cz = polyPoints.reduce((s, p) => s + p[2], 0) / polyPoints.length;
          extraAnn = makeValueTextbox([cx, cy, cz], label);
        }
        break;
      }

      case PR_KIND.PENCIL: {
        // Freehand pencil — geometry only, never a measurement label (bug 25).
        const isClosed =
          coords.length >= 4 &&
          Math.abs(coords[0] - coords[coords.length - 2]) < 0.5 &&
          Math.abs(coords[1] - coords[coords.length - 1]) < 0.5;
        const polyPoints = isClosed ? points.slice(0, -1) : points;
        // statValues {} → invalidated:false → no computed length/area label (bug 25).
        ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "", {});
        ann.data.contour = { polyline: polyPoints, closed: isClosed };
        // Belt-and-suspenders: freehandGetTextLines returns no text for a flagged
        // pencil even if Cornerstone ever recomputes its stats — a PR pencil must
        // NEVER show a dimension (bug 25).
        ann.data.suppressStats = true;
        break;
      }

      case PR_KIND.CIRCLE:
        if (points.length >= 2)
          ann = makeAnnotation(T.CircleROITool.toolName, [points[0], points[1]], imageId, frameOfReferenceUID, displayLabel,
            _statsFromStoredValue(T.CircleROITool.toolName, label));
        break;

      case PR_KIND.ELLIPSE: {
        // DICOM PS3.3 C.10.5.1.2: ELLIPSE has exactly 4 graphic points stored as
        // two axis endpoint pairs: [majorP0, majorP1, minorP0, minorP1].
        // MedDream (Advance Viewer) may store only 2 points (bounding-box corners:
        // [tlCol, tlRow, brCol, brRow]) instead of 4 axis-endpoint pairs — this is
        // a non-standard but common shortcut. Handle both:
        //   2-point: coords = [tlCol, tlRow, brCol, brRow]  → bounding box directly
        //   4-point: coords = [ax0col, ax0row, ax1col, ax1row, ax2col, ax2row, ax3col, ax3row]
        //            → derive bounding box from min/max of all 4 points
        // EllipticalROITool.getCanvasEllipseCorners expects [bottom, top, left, right]
        // which maps to the 4 cardinal edge midpoints of the bounding box.
        let ellMinX, ellMaxX, ellMinY, ellMaxY;
        if (coords.length >= 8) {
          const pxPairs = [
            [coords[0], coords[1]],
            [coords[2], coords[3]],
            [coords[4], coords[5]],
            [coords[6], coords[7]],
          ];
          ellMinX = Math.min(...pxPairs.map(p => p[0]));
          ellMaxX = Math.max(...pxPairs.map(p => p[0]));
          ellMinY = Math.min(...pxPairs.map(p => p[1]));
          ellMaxY = Math.max(...pxPairs.map(p => p[1]));
        } else if (coords.length >= 4) {
          // 2-point bounding-box format [tlCol, tlRow, brCol, brRow]
          ellMinX = Math.min(coords[0], coords[2]);
          ellMaxX = Math.max(coords[0], coords[2]);
          ellMinY = Math.min(coords[1], coords[3]);
          ellMaxY = Math.max(coords[1], coords[3]);
        }
        if (ellMinX != null && ellMaxX > ellMinX && ellMaxY > ellMinY) {
          const centerX = (ellMinX + ellMaxX) / 2;
          const centerY = (ellMinY + ellMaxY) / 2;
          // Apply the anisotropic column correction (no-op when colAspectFix == 1)
          // so a scout's non-square pixels don't stretch/shift the oval (see the
          // colAspectFix note at the top of applyGraphicAnnotations).
          const ptBottom = pixelToWorld(imageId, centerX * colAspectFix, ellMaxY);
          const ptTop    = pixelToWorld(imageId, centerX * colAspectFix, ellMinY);
          const ptLeft   = pixelToWorld(imageId, ellMinX * colAspectFix, centerY);
          const ptRight  = pixelToWorld(imageId, ellMaxX * colAspectFix, centerY);
          // Let the native EllipticalROITool recompute its OWN stats on load (Area /
          // Mean / Max / Std Dev, stacked) for parity with a Basic-viewer-drawn
          // ellipse. (Previously we passed the stored area, which showed ONLY Area
          // and — together with a separate TEXT annotation — duplicated the value on
          // one line.) Clear cachedStats and mark invalidated so Cornerstone computes
          // the full stat set from pixel data on first render — makeAnnotation would
          // otherwise pre-seed an area-only cachedStats and skip the recompute.
          ann = makeAnnotation(T.EllipticalROITool.toolName,
            [ptBottom, ptTop, ptLeft, ptRight], imageId, frameOfReferenceUID, "");
          ann.data.cachedStats = {};
          ann.invalidated = true;
        }
        break;
      }

      case PR_KIND.POLYLINE:
      default: {
        // Open / multi-point polyline (e.g. MedDream "Measurement polyline").
        const isClosed =
          coords.length >= 4 &&
          Math.abs(coords[0] - coords[coords.length - 2]) < 0.5 &&
          Math.abs(coords[1] - coords[coords.length - 1]) < 0.5;
        const polyPoints = isClosed ? points.slice(0, -1) : points;
        // statValues {} → invalidated:false → PlanarFreehand does NOT recompute
        // (recomputing on an unlocked freehand with empty handles causes it to
        // vanish when moved, because the edit loop has no handle points to work
        // with). Show the stored (0070,0006) value verbatim via storedLabel —
        // freehandGetTextLines returns it directly, bypassing roundNumber()
        // truncation (bug 15). The fmt() in freehandGetTextLines strips trailing
        // zeros (427.70 → 427.7) so the value matches MedDream's display.
        ann = makeAnnotation(T.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, "", {});
        ann.data.contour = { polyline: polyPoints, closed: isClosed };
        // Compute the path length from world-space geometry once at load time.
        // This matches MedDream's live-displayed value (both recompute from the
        // same pixel coordinates → same result). storedLabel prevents further
        // recompute on interaction, so the annotation never disappears on move.
        if (polyPoints.length >= 2) {
          const dist3 = (a, b) => Math.hypot(b[0]-a[0], b[1]-a[1], b[2]-a[2]);
          let pathLen = 0;
          for (let i = 1; i < polyPoints.length; i++) pathLen += dist3(polyPoints[i-1], polyPoints[i]);
          const planeMeta = cornerstone.metaData.get("imagePlaneModule", imageId);
          const unit = planeMeta?.rowPixelSpacing != null ? "mm" : "px";
          ann.data.storedLabel = `${parseFloat(pathLen.toFixed(2))} ${unit}`;
        } else if (label) {
          ann.data.storedLabel = label;
        }
        break;
      }
    }
    // Assign deterministic UIDs and add idempotently: if an annotation with this
    // UID already exists (same PR applied again), skip the push instead of
    // cloning it. This is what stops measurements multiplying on move (bug 16)
    // and appearing twice (bug 4).
    if (ann) {
      ann.annotationUID = `${prKey}#${idx}${uidSuffix}`;
      if (!annotationManager.getAnnotation(ann.annotationUID)) {
        annotationManager.addAnnotation(ann);
      }
      // Do NOT lock via the Cornerstone registry — annotations are movable by design.
      // Pencil renders as a bold stroke in MedDream (thickness 6.0); give the
      // restored freehand a heavier line so it reads as a pencil, not a thin
      // measurement line (bug 25).
      if (plain) {
        try {
          annotation.config.style.setAnnotationStyles(ann.annotationUID, plainObjectStyle({
            isText: type.toUpperCase() === "TEXT", color, boxed,
          }));
        } catch (_) {}
      } else if (kind === PR_KIND.PENCIL) {
        try {
          annotation.config.style.setAnnotationStyles(ann.annotationUID, { lineWidth: "3" });
        } catch (_) {}
      }
      trackAnnotation(imageId, ann.annotationUID, prUrl);
    }
    if (extraAnn) {
      extraAnn.annotationUID = `${prKey}#${idx}${uidSuffix}:text`;
      if (!annotationManager.getAnnotation(extraAnn.annotationUID)) {
        annotationManager.addAnnotation(extraAnn);
      }
      // Do NOT lock — text labels are also movable.
      trackAnnotation(imageId, extraAnn.annotationUID, prUrl);
    }
  };

  prData.graphicAnnotations.forEach((entry, idx) => {
    const resolved = resolveTargets?.(entry);
    const targets = resolved?.length ? resolved : [defaultImageId];
    targets.forEach((imageId) =>
      placeGraphicAnnotation(entry, idx, imageId, targets.length > 1 ? `@${imageId}` : "")
    );
  });
};

// ─── Weasis annotation rendering ─────────────────────────────────────────────

const applyWeasisAnnotations = (prData, imageId, frameOfReferenceUID, prUrl = null) => {
  if (!prData.weasisAnnotations?.length) return;
  const annotationManager = annotation.state.getAnnotationManager();

  // Stable-identity prefix (see applyGraphicAnnotations) — keep the Weasis path
  // idempotent too so re-applying never duplicates (bugs 4, 16).
  const prKey = prData.sopInstanceUID || prUrl || "pr";

  prData.weasisAnnotations.forEach(({ type, points, label, color }, idx) => {
    const worldPoints = points.map((p) => pixelToWorld(imageId, p.x, p.y));
    if (!worldPoints.length) return;

    let ann = null;

    switch (type) {
      // ── Lines / polylines ──────────────────────────────────────────────────
      case "LINE":
        ann = makeAnnotation(cornerstonetools.LengthTool.toolName, [worldPoints[0], worldPoints[1]], imageId, frameOfReferenceUID, label);
        break;

      case "POLYLINE":
        // Weasis polylines can be length measurements (2 logical pts + decorative ticks)
        // or actual multi-point paths. Use PlanarFreehandROI to render all points faithfully.
        // handles.points must be [] — non-empty means "drawing in progress" (contour lost on interaction).
        ann = makeAnnotation(cornerstonetools.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, label);
        ann.data.contour = { polyline: worldPoints, closed: false };
        break;

      // ── ROI shapes ─────────────────────────────────────────────────────────
      case "ELLIPSE":
        // Weasis ellipse: 4 pts — (topLeft, bottomRight) bounding box corners
        if (worldPoints.length >= 4)
          ann = makeAnnotation(cornerstonetools.EllipticalROITool.toolName, [worldPoints[0], worldPoints[1], worldPoints[2], worldPoints[3]], imageId, frameOfReferenceUID, label);
        break;

      case "RECTANGLE":
        if (worldPoints.length >= 4) {
          ann = makeAnnotation(cornerstonetools.PlanarFreehandROITool.toolName, [], imageId, frameOfReferenceUID, label);
          ann.data.contour = { polyline: worldPoints, closed: true };
        }
        break;

      // ── Point ──────────────────────────────────────────────────────────────
      case "POINT":
        ann = makeAnnotation(cornerstonetools.ProbeTool.toolName, [worldPoints[0]], imageId, frameOfReferenceUID, label);
        break;

      // ── Angles ─────────────────────────────────────────────────────────────
      case "ANGLE":
        if (worldPoints.length >= 3)
          ann = makeAnnotation(cornerstonetools.AngleTool.toolName, [worldPoints[0], worldPoints[1], worldPoints[2]], imageId, frameOfReferenceUID, label);
        break;

      case "COBBANGLE":
        if (worldPoints.length >= 4)
          ann = makeAnnotation(cornerstonetools.CobbAngleTool.toolName, [worldPoints[0], worldPoints[1], worldPoints[2], worldPoints[3]], imageId, frameOfReferenceUID, label);
        break;

      default:
        console.warn(`[Weasis PR] Unmapped graphic type: "${type}"`, { points, label });
        break;
    }

    if (ann) {
      ann.annotationUID = `${prKey}#weasis#${idx}`;
      if (!annotationManager.getAnnotation(ann.annotationUID)) {
        annotationManager.addAnnotation(ann);
      }
      // Do NOT lock — annotations are movable.
      trackAnnotation(imageId, ann.annotationUID, prUrl);
      // Apply the Weasis annotation colour (AARRGGBB → #RRGGBB)
      if (color) {
        annotation.config.style.setAnnotationStyles(ann.annotationUID, {
          color,
          colorSelected: color,
          colorHighlighted: color,
          textBoxColor: color,
          textBoxColorSelected: color,
        });
      }
    }
  });
};

// ─── Displayed Area (pan / zoom) ─────────────────────────────────────────────

/**
 * Apply the Displayed Area Selection to the viewport camera so only the
 * specified pixel rectangle is shown (SCALE TO FIT behaviour).
 *
 * DICOM (0070,005A):
 *   topLeft     = [col, row] of the top-left visible pixel
 *   bottomRight = [col, row] of the bottom-right visible pixel
 */
const applyDisplayedArea = (displayedArea, viewport, imageId) => {
  const { topLeft, bottomRight } = displayedArea;
  const mode = (displayedArea.presentationSizeMode || "SCALE TO FIT").toUpperCase();
  const mag  = displayedArea.magnificationRatio;
  const [tlCol, tlRow] = topLeft;
  const [brCol, brRow] = bottomRight;

  // Reset camera first so parallelScale is at a known baseline (full image fits).
  viewport.resetCamera();
  const baseCamera = viewport.getCamera();
  if (baseCamera?.parallelScale == null) return;

  // Pixel spacing — try imagePlaneModule first, fall back to imagePixelModule.
  const planeMeta  = cornerstone.metaData.get("imagePlaneModule",  imageId);
  const pixelMeta  = cornerstone.metaData.get("imagePixelModule",  imageId);
  const rowSpacing = planeMeta?.rowPixelSpacing    ?? pixelMeta?.rowPixelSpacing    ?? 1;
  const colSpacing = planeMeta?.columnPixelSpacing ?? pixelMeta?.columnPixelSpacing ?? 1;

  // CR/DX/SC images carry NO patient-space geometry (no ImagePositionPatient /
  // ImageOrientationPatient), so pixelToWorld() falls back to raw pixel indices
  // [col,row,0]. But the viewport renders the image in world space scaled by pixel
  // spacing (e.g. a 1024px image at 0.168mm spans 0..172mm), so a pixel-index focal
  // point lands far outside the image → the image flies up-left out of the viewport
  // and nothing renders (annotations / heatmap / outline included). Without a real
  // plane we cannot map the displayed-area corners to the camera's world space, so
  // keep the resetCamera() fit-to-image (which is also what a full-image MAGNIFY PR
  // intends) and skip the camera override.
  const hasPlaneGeometry =
    !!(planeMeta?.imagePositionPatient && planeMeta?.rowCosines && planeMeta?.columnCosines);
  if (!hasPlaneGeometry) return;

  const canvas   = viewport.canvas;
  const vpWidth  = canvas.clientWidth  || canvas.width  || 512;
  const vpHeight = canvas.clientHeight || canvas.height || 512;
  const vpAspect = vpWidth / vpHeight;

  // DICOM corners are 1-based. Convert to 0-based pixel indices before calling
  // pixelToWorld, which expects 0-based. The center of the displayed area in
  // 0-based space is ((tl-1 + br-1)/2) = (tl+br)/2 - 1.
  const centerCol = (tlCol + brCol) / 2 - 1;
  const centerRow = (tlRow + brRow) / 2 - 1;
  const newFocalPoint = pixelToWorld(imageId, centerCol, centerRow);

  let newParallelScale;
  if (mode === "MAGNIFY" && mag && mag > 0) {
    // mag = display pixels per image pixel (height axis, same as the save path).
    // parallelScale = half world height = (vpHeight/2) * rowSpacing / mag.
    newParallelScale = (vpHeight / 2) * rowSpacing / mag;
  } else {
    // SCALE TO FIT: scale the displayed rectangle to fill the viewport.
    // Width of displayed area in 0-based pixels = (br-1) - (tl-1) = br - tl.
    const displayedWidth  = Math.abs(brCol - tlCol);
    const displayedHeight = Math.abs(brRow - tlRow);
    if (displayedWidth <= 0 || displayedHeight <= 0) return;
    const dispWorldWidth  = displayedWidth  * colSpacing;
    const dispWorldHeight = displayedHeight * rowSpacing;
    const dispAspect      = dispWorldWidth / dispWorldHeight;
    // Choose the constraining dimension so the whole displayed area fits.
    newParallelScale =
      dispAspect > vpAspect
        ? (dispWorldWidth / vpAspect) / 2   // width-constrained
        : dispWorldHeight / 2;              // height-constrained
  }

  // Preserve the camera view-direction vector (position = focalPoint + viewOffset).
  const viewOffset = [
    baseCamera.position[0] - baseCamera.focalPoint[0],
    baseCamera.position[1] - baseCamera.focalPoint[1],
    baseCamera.position[2] - baseCamera.focalPoint[2],
  ];
  const newPosition = [
    newFocalPoint[0] + viewOffset[0],
    newFocalPoint[1] + viewOffset[1],
    newFocalPoint[2] + viewOffset[2],
  ];

  viewport.setCamera({
    ...baseCamera,
    parallelScale: newParallelScale,
    focalPoint:    newFocalPoint,
    position:      newPosition,
  });
};

// ─── Frame matching ───────────────────────────────────────────────────────────

/**
 * SOP Instance UID of an imageId. Matched only via exact SOP UID — never URL
 * substrings: DICOM UIDs are hierarchical so "1.2.3.4.5" is always a substring
 * of "1.2.3.4.50", which would apply annotations to the wrong image.
 *
 * Cornerstone's sopCommonModule is populated once an image is (being) loaded;
 * the study-load registry covers instances not loaded yet. It is keyed by the
 * stack slot id, so per-frame ids are mapped back to it (wadouri `?frame=N` →
 * bare id, wadors `/frames/N` → `/frames/1`).
 */
const registeredInstanceOf = (imageId) => {
  const slotId = imageId.startsWith("wadors:")
    ? imageId.replace(/\/frames\/\d+$/, "/frames/1")
    : imageId.replace(/[?&]frame=\d+$/, "");
  return getImageMetadata(imageId) ?? getImageMetadata(slotId);
};

const sopOfImageId = (imageId) => {
  if (typeof imageId !== "string" || !imageId) return null;
  const meta = cornerstone.metaData.get("sopCommonModule", imageId);
  const metaSop = (meta?.sopInstanceUID ?? meta?.SOPInstanceUID)?.trim();
  if (metaSop) return metaSop;
  return registeredInstanceOf(imageId)?.sopUid?.trim() || null;
};

/**
 * 1-based frame number an imageId shows when it belongs to a MULTI-frame
 * instance, else null (single-frame images carry no Referenced Frame Number).
 */
const multiFrameNumberOf = (imageId) => {
  if (typeof imageId !== "string" || !imageId) return null;
  const mf = cornerstone.metaData.get("multiframeModule", imageId);
  const frames = Number(mf?.NumberOfFrames) || registeredInstanceOf(imageId)?.numberOfFrames || 0;
  return frames > 1 ? getImageFrameIndex(imageId) + 1 : null;
};

/**
 * Every image a viewport can display, indexed SOP UID → 1-based frame → imageIds.
 * Covers the live stack plus — for a mixed series in OUTER mode, whose live
 * stack holds only one slot per instance — every frame of every multi-frame
 * instance, so a PR can target a frame the viewport is not showing yet.
 * Several ids can address the same frame (e.g. the OUTER slot and the INNER
 * frame-1 id); the annotation is placed on each so it renders in either mode.
 * Falls back to the series stack when the viewport has no images yet.
 */
const collectViewportFrames = (viewport, stack) => {
  const bySop = new Map();
  const add = (imageId, frameNumber, sop) => {
    if (typeof imageId !== "string" || !imageId) return;
    const s = sop || sopOfImageId(imageId);
    if (!s) return;
    if (!bySop.has(s)) bySop.set(s, new Map());
    const frames = bySop.get(s);
    const n = frameNumber ?? getImageFrameIndex(imageId) + 1;
    if (!frames.has(n)) frames.set(n, new Set());
    frames.get(n).add(imageId);
  };

  (viewport?.getImageIds?.() ?? []).forEach((id) => add(id));
  const mixed = viewport ? getMixedSeriesInstances(viewport.id) : null;
  mixed?.forEach(({ outerImageId, frames }) => {
    const sop = sopOfImageId(outerImageId);
    add(outerImageId, 1, sop);
    frames?.forEach((id, i) => add(id, i + 1, sop));
  });
  if (!bySop.size) (stack?.imageIds ?? []).forEach((id) => add(id));
  return bySop;
};

/** imageIds for image references [{ sop, frames }] — frames [] = every frame. */
const resolveRefImageIds = (bySop, refs) => {
  const out = new Set();
  for (const { sop, frames } of refs ?? []) {
    const perFrame = bySop.get(sop);
    if (!perFrame) continue;
    if (!frames?.length) perFrame.forEach((ids) => ids.forEach((id) => out.add(id)));
    else frames.forEach((n) => perFrame.get(n)?.forEach((id) => out.add(id)));
  }
  return [...out];
};

/**
 * The image (+ frames) a PR applies to when an annotation item carries no
 * reference of its own: the first top-level referenced image present in the
 * viewport. (Only the FIRST — Augmento PRs list spine-label images at the top
 * level too, and plain measurements must not spread onto those.)
 */
const primaryRef = (prData, bySop) => {
  const refs = prData.referencedImages?.length
    ? prData.referencedImages
    : (prData.referencedSOPInstanceUIDs ?? []).map((sop) => ({ sop: sop.trim(), frames: [] }));
  return refs.find((r) => bySop.has(r.sop)) ?? null;
};

/**
 * Frames a PR is explicitly scoped to, as [{ sop, frameNumber }] (1-based),
 * most specific first: annotation-item references, then top-level references.
 * Empty when the PR names no frame (single-frame images, or "all frames").
 * Used to land the viewport on the frame a multi-frame PR was drawn on.
 */
export const getPRFrameTargets = (prData) => {
  if (!prData) return [];
  const refs = [
    ...(prData.graphicAnnotations ?? []).flatMap((a) => a.refs ?? []),
    ...(prData.spineLabels ?? [])
      .filter((s) => s.frameNumber)
      .map((s) => ({ sop: s.sopInstanceUID, frames: [s.frameNumber] })),
    ...(prData.referencedImages ?? []),
  ];
  const seen = new Set();
  const out = [];
  for (const { sop, frames } of refs) {
    if (!sop || !frames?.length) continue;
    const frameNumber = Math.min(...frames);
    const key = `${sop}|${frameNumber}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ sop, frameNumber });
  }
  return out;
};

/** True when `imageId` is an image of the instance `sop` (any frame). */
export const imageIdBelongsToSop = (imageId, sop) =>
  !!sop && sopOfImageId(imageId) === sop.trim();

// ─── Main apply entry point ───────────────────────────────────────────────────

/**
 * Apply parsed PR data to a viewport.
 * @param {object}  prData        - parsed PR from parsePRFile
 * @param {number}  viewportIndex
 * Annotations are placed on the frame(s) each graphic item references —
 * (0008,1160) Referenced Frame Number — so on a multi-frame clip they show only
 * on their own frame. The viewport state (VOI / transform / displayed area) is
 * applied only while the viewport shows the referenced IMAGE (any of its frames,
 * so it stays steady through a cine loop); navigation to the PR's frame is the
 * caller's job (getPRFrameTargets + focusInstanceFrame).
 *
 * @param {object}  prData        - parsed PR from parsePRFile
 * @param {number}  viewportIndex
 * @param {object}  [stack]       - the series stack (fallback when the viewport has no images yet)
 * @param {object}  [options]
 * @param {string}  [options.prUrl=null]   - source URL of the PR file; used to track annotations
 *                                           per-PR so individual PRs can be toggled on/off.
 * @returns {boolean} true if applied, false if the referenced image was not found in the viewport
 */
export const applyPRToViewport = async (prData, viewportIndex, stack, { prUrl = null } = {}) => {
  if (!prData) return false;

  const viewport = await getViewport(viewportIndex);
  if (!viewport) return false;

  // ── Resolve the referenced image(s) ───────────────────────────────────────
  // Nothing referenced → nothing to place (unchanged behaviour).
  if (!prData.referencedSOPInstanceUIDs?.length) return true;
  const bySop = collectViewportFrames(viewport, stack);
  const ref = primaryRef(prData, bySop);
  // PR does not reference any image in this series — skip entirely.
  if (!ref) return false;

  // When the pencil toggle is OFF, the viewport was already reset to its
  // original windowing and zoom/pan on toggle-off — skip all PR application.
  if (!_annotationsEnabled) {
    return true;
  }

  const refImageIds = resolveRefImageIds(bySop, [ref]);
  // ── Guard: no imageId means we couldn't identify the referenced frame ─────
  // Skip everything — don't corrupt the current frame's display state.
  if (!refImageIds.length) {
    return true;
  }

  // Apply the PR's windowing / zoom only while the viewport shows the referenced
  // image — never onto an unrelated image the viewport happens to be on (the
  // annotations are still tagged so they appear when the user gets there).
  const currentImageId = viewport.getCurrentImageId();
  const applyViewportState = imageIdBelongsToSop(currentImageId, ref.sop);

  // Geometry source: the frame on screen when it is the referenced image (all
  // frames of an instance share geometry), else the referenced frame itself.
  const imageId = applyViewportState ? currentImageId : refImageIds[0];
  const imagePlane = cornerstone.metaData.get("imagePlaneModule", imageId);
  const frameOfReferenceUID = imagePlane?.frameOfReferenceUID ?? "";

  // ── Single-pass viewport-state apply (VOI LUT + Spatial Transform + Displayed
  // Area), keyed to the stored values and image geometry and committed with ONE
  // render. Doing these together (rather than three separate passes each with
  // its own render) is what makes windowing and zoom/pan apply *together* and
  // makes the displayed-area camera the final authority — independent of how
  // many viewports are open (bug 1).
  if (applyViewportState) {
    const props = {};
    if (prData.voiLUT) {
      // VOI LUT applied via setProperties (not setVOILUTRange) so it joins this
      // single pass instead of triggering its own intermediate render.
      props.voiRange = {
        lower: prData.voiLUT.windowCenter - prData.voiLUT.windowWidth / 2,
        upper: prData.voiLUT.windowCenter + prData.voiLUT.windowWidth / 2,
      };
    }
    if (prData.horizontalFlip) props.flipHorizontal = true;
    if (prData.rotation)       props.rotation = prData.rotation;
    if (Object.keys(props).length) viewport.setProperties(props);

    // Displayed area sets the camera LAST so no intervening reset/render can
    // recompute zoom against the live viewport size.
    if (prData.displayedArea) {
      applyDisplayedArea(prData.displayedArea, viewport, imageId);
    }
  }

  // Each graphic item lands on the frame(s) it references; items without their
  // own reference follow the PR's primary image (all its referenced frames).
  applyGraphicAnnotations(prData, viewport, refImageIds[0], frameOfReferenceUID, prUrl,
    (entry) => resolveRefImageIds(bySop, entry.refs?.length ? entry.refs : [ref]));
  // Weasis objects carry no per-object reference — primary referenced frame.
  applyWeasisAnnotations(prData, refImageIds[0], frameOfReferenceUID, prUrl);

  // Single commit for the whole viewport state + annotations.
  viewport.render();
  return true;
};

/**
 * Re-apply only the displayed area (pan / zoom) for a PR that has already had
 * its annotations placed.  Call this when the viewport camera was reset by a
 * subsequent loadViewport call but the annotation objects are still present.
 */
export const reapplyPRCamera = async (prData, viewportIndex, imageId) => {
  if (!prData?.displayedArea) return;
  const viewport = await getViewport(viewportIndex);
  if (!viewport) return;
  applyDisplayedArea(prData.displayedArea, viewport, imageId);
  viewport.render();
};

/**
 * Re-apply only the VIEWPORT STATE of a PR (VOI LUT, spatial transforms, pan/zoom)
 * WITHOUT touching annotations.  Used when unchecking a PR in the list panel to
 * restore the viewport state from the remaining selected PRs after a full reset.
 */
export const reapplyPRViewportState = async (prData, viewportIndex) => {
  if (!prData) return;
  const viewport = await getViewport(viewportIndex);
  if (!viewport) return;
  const imageId = viewport.getCurrentImageId();
  if (!imageId) return;

  // Single-pass apply (same ordering as applyPRToViewport) so that when the
  // viewport count / layout changes, VOI + transform + displayed-area camera are
  // re-applied together and the camera is keyed to the stored corners, not the
  // new viewport size (bug 1).
  const props = {};
  if (prData.voiLUT) {
    props.voiRange = {
      lower: prData.voiLUT.windowCenter - prData.voiLUT.windowWidth / 2,
      upper: prData.voiLUT.windowCenter + prData.voiLUT.windowWidth / 2,
    };
  }
  if (prData.horizontalFlip) props.flipHorizontal = true;
  if (prData.rotation)       props.rotation = prData.rotation;
  if (Object.keys(props).length) viewport.setProperties(props);

  if (prData.displayedArea) applyDisplayedArea(prData.displayedArea, viewport, imageId);
  viewport.render();
};

/**
 * Convert a PR's parsed spine labels (pixel coords + referenced SOP) into the
 * native spine-overlay format used by ViewportContext.spineLabels, so a loaded
 * spine-labelling PR renders as styled chips (not as measurement annotations).
 *
 * Each label is placed on the image it references (labels may span multiple
 * images): we resolve that image in the stack, convert its pixel anchor to world
 * space, and tag it with the image's frameOfReferenceUID + the source PR url (for
 * removal when the PR is toggled off).
 *
 * @returns Array<{ worldPos, vertebra, excluded, frameOfReferenceUID, viewportIndex, imageId, sopInstanceUID, prUrl }>
 */
export const buildSpineOverlayLabels = (prData, stack, viewportIndex, prUrl = null) => {
  if (!prData?.spineLabels?.length || !stack) return [];
  let viewport = null;
  try { viewport = getViewportByIdAndDefRenderingEngine("viewport_" + viewportIndex); } catch (_) {}
  const bySop = collectViewportFrames(viewport, stack);
  const out = [];
  for (const s of prData.spineLabels) {
    if (!s?.vertebra || s.col == null || s.row == null) continue;

    // Resolve the image (and multi-frame frame) this label belongs to by its
    // referenced SOP, falling back to the PR's primary referenced image, then
    // the first image in the stack.
    let imageId = null;
    if (s.sopInstanceUID) {
      imageId = resolveRefImageIds(bySop, [
        { sop: s.sopInstanceUID, frames: s.frameNumber ? [s.frameNumber] : [1] },
      ])[0] ?? null;
    }
    if (!imageId) {
      const ref = primaryRef(prData, bySop);
      imageId = (ref ? resolveRefImageIds(bySop, [ref])[0] : stack.imageIds?.[0]) ?? null;
    }
    if (!imageId) continue;

    const worldPos = pixelToWorld(imageId, s.col, s.row);
    const plane = cornerstone.metaData.get("imagePlaneModule", imageId);
    out.push({
      worldPos,
      vertebra: s.vertebra,
      excluded: false,
      frameOfReferenceUID: plane?.frameOfReferenceUID ?? null,
      viewportIndex,
      imageId,
      sopInstanceUID: s.sopInstanceUID ?? null,
      prUrl,
    });
  }
  return out;
};

/** Clear the PR parse cache (call on study unload if needed). */
export const clearPRCache = () => prCache.clear();
